/**
 * @license Angular l10n
 * Copyright Roberto Simonetti
 * MIT license
 * https://github.com/robisim74/angular-l10n
 */

import { Injectable, Injector, InjectionToken, Inject, Optional, ChangeDetectorRef, Pipe, Input, Directive, ElementRef, Renderer2, forwardRef, Component, HostBinding, NgModule } from '@angular/core';
import { ReplaySubject, Subject, Observable, BehaviorSubject, race, combineLatest, merge, concat } from 'rxjs';
import { __awaiter, __generator, __extends } from 'tslib';
import { Router, NavigationStart, NavigationEnd } from '@angular/router';
import { Location } from '@angular/common';
import { filter, timeout, map, takeUntil } from 'rxjs/operators';
import { HttpClient, HttpHeaders, HttpParams, HTTP_INTERCEPTORS } from '@angular/common/http';
import { Title, Meta, DomSanitizer } from '@angular/platform-browser';
import { NG_VALIDATORS } from '@angular/forms';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Allows to get the dependencies at the root level.
 */
var InjectorRef = /** @class */ (function () {
    function InjectorRef(injector) {
        this.injector = injector;
        /**
         * TranslationService instances.
         */
        this.translations = [];
        InjectorRef.injector = this.injector;
    }
    /**
     * @template T
     * @param {?} token
     * @param {?=} notFoundValue
     * @return {?}
     */
    InjectorRef.get = /**
     * @template T
     * @param {?} token
     * @param {?=} notFoundValue
     * @return {?}
     */
    function (token, notFoundValue) {
        return InjectorRef.injector.get(token, notFoundValue);
    };
    InjectorRef.injector = Injector.NULL;
    InjectorRef.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    InjectorRef.ctorParameters = function () { return [
        { type: Injector }
    ]; };
    return InjectorRef;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @type {?} */
var L10N_CONFIG = new InjectionToken('L10N_CONFIG');
/**
 * @param {?} l10nConfig
 * @return {?}
 */
function l10nConfigFactory(l10nConfig) {
    return {
        locale: l10nConfig.locale || {},
        translation: l10nConfig.translation || {},
        logger: l10nConfig.logger || {},
        localizedRouting: l10nConfig.localizedRouting || {},
        search: l10nConfig.search || {},
        localeInterceptor: l10nConfig.localeInterceptor || {}
    };
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/** @enum {number} */
var StorageStrategy = {
    Session: 0,
    Local: 1,
    Cookie: 2,
    Disabled: 3,
};
StorageStrategy[StorageStrategy.Session] = 'Session';
StorageStrategy[StorageStrategy.Local] = 'Local';
StorageStrategy[StorageStrategy.Cookie] = 'Cookie';
StorageStrategy[StorageStrategy.Disabled] = 'Disabled';
/** @enum {number} */
var ProviderType = {
    Fallback: 0,
    Static: 1,
    WebAPI: 2,
};
ProviderType[ProviderType.Fallback] = 'Fallback';
ProviderType[ProviderType.Static] = 'Static';
ProviderType[ProviderType.WebAPI] = 'WebAPI';
/** @enum {string} */
var ISOCode = {
    Language: 'languageCode',
    Country: 'countryCode',
    Script: 'scriptCode',
};
/** @enum {string} */
var ExtraCode = {
    NumberingSystem: 'numberingSystem',
    Calendar: 'calendar',
    Currency: 'currency',
    Timezone: 'timezone',
};
/** @enum {string} */
var LogLevel = {
    Error: 'error',
    Warn: 'warn',
    Info: 'info',
    Log: 'log',
    Off: 'off',
};
/** @enum {number} */
var NumberFormatStyle = {
    Decimal: 0,
    Percent: 1,
    Currency: 2,
};
NumberFormatStyle[NumberFormatStyle.Decimal] = 'Decimal';
NumberFormatStyle[NumberFormatStyle.Percent] = 'Percent';
NumberFormatStyle[NumberFormatStyle.Currency] = 'Currency';
/** @type {?} */
var NUMBER_FORMAT_REGEXP = /^(\d+)?\.((\d+)(\-(\d+))?)?$/;
/** @type {?} */
var ISO8601_DATE_REGEX = /^(\d{4})-?(\d\d)-?(\d\d)(?:T(\d\d)(?::?(\d\d)(?::?(\d\d)(?:\.(\d+))?)?)?(Z|([+-])(\d\d):?(\d\d))?)?$/;
/** @type {?} */
var FORMAT_ALIASES = {
    'short': { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' },
    'medium': { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' },
    'long': { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' },
    'full': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' },
    'shortDate': { year: 'numeric', month: 'numeric', day: 'numeric' },
    'mediumDate': { year: 'numeric', month: 'short', day: 'numeric' },
    'longDate': { year: 'numeric', month: 'long', day: 'numeric' },
    'fullDate': { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' },
    'shortTime': { hour: 'numeric', minute: 'numeric' },
    'mediumTime': { hour: 'numeric', minute: 'numeric', second: 'numeric' }
};
/** @type {?} */
var LOG_MESSAGES = {
    'missingOnInit': "Missing 'ngOnInit' method: required by AoT compilation",
    'missingOnDestroy': "Missing 'ngOnDestroy' method to cancel subscriptions: required by AoT compilation",
    'missingLang': "Missing 'lang' parameter",
    'missingDefaultLocale': "Missing 'defaultLocale' parameter",
    'missingCurrency': "Missing 'currency' parameter",
    'invalidNumberFormatAlias': "Invalid number format alias: the default format will be used",
    'invalidDateFormatAlias': "Invalid date format alias: the default format will be used"
};

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var Logger = /** @class */ (function () {
    function Logger(configuration) {
        var _this = this;
        this.configuration = configuration;
        Logger.level = this.configuration.logger.level || LogLevel.Off;
        if (Logger.level != LogLevel.Off) {
            Logger.buffer.subscribe(function (log) {
                _this.send(log);
            });
        }
    }
    /**
     * @param {?} name
     * @param {?} message
     * @return {?}
     */
    Logger.log = /**
     * @param {?} name
     * @param {?} message
     * @return {?}
     */
    function (name, message) {
        if (Logger.level == LogLevel.Off)
            return;
        Logger.buffer.next({ name: name, message: message });
    };
    /**
     * @param {?} log
     * @return {?}
     */
    Logger.prototype.send = /**
     * @param {?} log
     * @return {?}
     */
    function (log) {
        /** @type {?} */
        var message = "angular-l10n (" + log.name + "): " + LOG_MESSAGES[log.message];
        ((/** @type {?} */ (console)))[(/** @type {?} */ (Logger.level))](message);
    };
    Logger.level = null;
    Logger.buffer = new ReplaySubject();
    Logger.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Logger.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] }
    ]; };
    return Logger;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var Caching = /** @class */ (function () {
    function Caching() {
        this.cache = {};
    }
    /**
     * @param {?} key
     * @param {?} request
     * @return {?}
     */
    Caching.prototype.read = /**
     * @param {?} key
     * @param {?} request
     * @return {?}
     */
    function (key, request) {
        var _this = this;
        /** @type {?} */
        var cached = this.cache[key];
        if (cached)
            return cached;
        /** @type {?} */
        var buffer = new ReplaySubject(1);
        request.subscribe(function (value) { return buffer.next(value); }, function (error) {
            buffer.error(error);
            _this.cache[key] = undefined;
        }, function () { return buffer.complete(); });
        /** @type {?} */
        var response = buffer.asObservable();
        this.cache[key] = response;
        return response;
    };
    Caching.decorators = [
        { type: Injectable }
    ];
    return Caching;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides the methods to check if Intl APIs are supported.
 */
var  /**
 * Provides the methods to check if Intl APIs are supported.
 */
IntlAPI = /** @class */ (function () {
    function IntlAPI() {
    }
    /**
     * @return {?}
     */
    IntlAPI.hasIntl = /**
     * @return {?}
     */
    function () {
        /** @type {?} */
        var hasIntl = typeof Intl === "object" && !!Intl;
        return hasIntl;
    };
    /**
     * @return {?}
     */
    IntlAPI.hasDateTimeFormat = /**
     * @return {?}
     */
    function () {
        return IntlAPI.hasIntl() && Intl.hasOwnProperty("DateTimeFormat");
    };
    /**
     * @return {?}
     */
    IntlAPI.hasTimezone = /**
     * @return {?}
     */
    function () {
        if (IntlAPI.hasIntl() && IntlAPI.hasDateTimeFormat()) {
            try {
                new Intl.DateTimeFormat('en-US', { timeZone: 'America/Los_Angeles' }).format(new Date());
            }
            catch (e) {
                return false;
            }
            return true;
        }
        return false;
    };
    /**
     * @return {?}
     */
    IntlAPI.hasNumberFormat = /**
     * @return {?}
     */
    function () {
        return IntlAPI.hasIntl() && Intl.hasOwnProperty("NumberFormat");
    };
    /**
     * @return {?}
     */
    IntlAPI.hasCollator = /**
     * @return {?}
     */
    function () {
        return IntlAPI.hasIntl() && Intl.hasOwnProperty("Collator");
    };
    return IntlAPI;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} name
 * @return {?}
 */
function getLocalStorage(name) {
    return localStorage.getItem(name);
}
/**
 * @param {?} name
 * @return {?}
 */
function getSessionStorage(name) {
    return sessionStorage.getItem(name);
}
/**
 * @param {?} name
 * @return {?}
 */
function getCookie(name) {
    /** @type {?} */
    var result = null;
    if (typeof document !== "undefined") {
        result = new RegExp("(?:^|; )" + encodeURIComponent(name) + "=([^;]*)").exec(document.cookie);
    }
    return result ? result[1] : null;
}
/**
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
function setLocalStorage(name, value) {
    localStorage.setItem(name, value);
}
/**
 * @param {?} name
 * @param {?} value
 * @return {?}
 */
function setSessionStorage(name, value) {
    sessionStorage.setItem(name, value);
}
/**
 * @param {?} name
 * @param {?} value
 * @param {?=} expiration
 * @return {?}
 */
function setCookie(name, value, expiration) {
    /** @type {?} */
    var expires = "";
    if (expiration != null) {
        /** @type {?} */
        var expirationDate = new Date();
        expirationDate.setTime(expirationDate.getTime() +
            (expiration * 24 * 60 * 60 * 1000));
        expires = "; expires=" + expirationDate.toUTCString();
    }
    if (typeof document !== "undefined") {
        document.cookie = name + "=" + value + expires + "; path=/";
    }
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Implement this class-interface to create a custom storage for default locale, currency & timezone.
 * @abstract
 */
var LocaleStorage = /** @class */ (function () {
    function LocaleStorage() {
    }
    LocaleStorage.decorators = [
        { type: Injectable }
    ];
    return LocaleStorage;
}());
var L10nStorage = /** @class */ (function () {
    function L10nStorage(configuration) {
        this.configuration = configuration;
        this.hasCookie = typeof navigator !== "undefined" && navigator.cookieEnabled;
        this.hasStorage = typeof Storage !== "undefined";
    }
    /**
     * @param {?} name
     * @return {?}
     */
    L10nStorage.prototype.read = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        return __awaiter(this, void 0, void 0, function () {
            var value;
            return __generator(this, function (_a) {
                value = null;
                if (this.configuration.locale.storage != StorageStrategy.Disabled) {
                    if (this.configuration.locale.storage == StorageStrategy.Local && this.hasStorage) {
                        value = getLocalStorage(this.getName(name));
                    }
                    else if (this.configuration.locale.storage == StorageStrategy.Session && this.hasStorage) {
                        value = getSessionStorage(this.getName(name));
                    }
                    else if (this.configuration.locale.storage == StorageStrategy.Cookie && this.hasCookie) {
                        value = getCookie(this.getName(name));
                    }
                }
                return [2 /*return*/, value];
            });
        });
    };
    /**
     * @param {?} name
     * @param {?} value
     * @return {?}
     */
    L10nStorage.prototype.write = /**
     * @param {?} name
     * @param {?} value
     * @return {?}
     */
    function (name, value) {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                if (this.configuration.locale.storage != StorageStrategy.Disabled) {
                    if (this.configuration.locale.storage == StorageStrategy.Local && this.hasStorage) {
                        setLocalStorage(this.getName(name), value);
                    }
                    else if (this.configuration.locale.storage == StorageStrategy.Session && this.hasStorage) {
                        setSessionStorage(this.getName(name), value);
                    }
                    else if (this.configuration.locale.storage == StorageStrategy.Cookie && this.hasCookie) {
                        setCookie(this.getName(name), value, this.configuration.locale.cookieExpiration);
                    }
                }
                return [2 /*return*/];
            });
        });
    };
    /**
     * @param {?} name
     * @return {?}
     */
    L10nStorage.prototype.getName = /**
     * @param {?} name
     * @return {?}
     */
    function (name) {
        if (this.configuration.locale.storageNames) {
            return ((/** @type {?} */ (this.configuration.locale.storageNames)))[name] || name;
        }
        return name;
    };
    L10nStorage.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    L10nStorage.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] }
    ]; };
    return L10nStorage;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var DefaultLocale$1 = /** @class */ (function () {
    function DefaultLocale() {
    }
    Object.defineProperty(DefaultLocale.prototype, "value", {
        get: /**
         * @return {?}
         */
        function () {
            return this.formattedValue;
        },
        set: /**
         * @param {?} defaultLocale
         * @return {?}
         */
        function (defaultLocale) {
            this.formattedValue = defaultLocale;
            this.parseValue();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @param {?} languageCode
     * @param {?=} countryCode
     * @param {?=} scriptCode
     * @param {?=} numberingSystem
     * @param {?=} calendar
     * @return {?}
     */
    DefaultLocale.prototype.build = /**
     * @param {?} languageCode
     * @param {?=} countryCode
     * @param {?=} scriptCode
     * @param {?=} numberingSystem
     * @param {?=} calendar
     * @return {?}
     */
    function (languageCode, countryCode, scriptCode, numberingSystem, calendar) {
        this.languageCode = languageCode;
        this.scriptCode = scriptCode;
        this.countryCode = countryCode;
        this.numberingSystem = numberingSystem;
        this.calendar = calendar;
        /** @type {?} */
        var value = languageCode;
        value += !!scriptCode ? "-" + scriptCode : "";
        value += !!countryCode ? "-" + countryCode : "";
        // Adds the 'u' (Unicode) extension.
        value += (!!numberingSystem || !!calendar) ? "-u" : "";
        value += !!numberingSystem ? "-nu-" + numberingSystem : "";
        value += !!calendar ? "-ca-" + calendar : "";
        this.formattedValue = value;
    };
    /**
     * @return {?}
     */
    DefaultLocale.prototype.parseValue = /**
     * @return {?}
     */
    function () {
        if (!!this.value) {
            /** @type {?} */
            var value = this.value;
            // Looks for the 'u' (Unicode) extension.
            /** @type {?} */
            var index = value.search("-u");
            if (index != -1) {
                /** @type {?} */
                var extensions = value.substring(index + 1).split("-");
                switch (extensions.length) {
                    case 3:
                        if (extensions[1] == "nu") {
                            this.numberingSystem = extensions[2];
                            this.calendar = undefined;
                        }
                        else if (extensions[1] == "ca") {
                            this.numberingSystem = undefined;
                            this.calendar = extensions[2];
                        }
                        break;
                    default:
                        this.numberingSystem = extensions[2];
                        this.calendar = extensions[4];
                }
                // Extracts the codes.
                value = value.substring(0, index);
            }
            /** @type {?} */
            var codes = value.split("-");
            switch (codes.length) {
                case 1:
                    this.languageCode = codes[0];
                    this.scriptCode = undefined;
                    this.countryCode = undefined;
                    break;
                case 2:
                    this.languageCode = codes[0];
                    this.scriptCode = undefined;
                    this.countryCode = codes[1];
                    break;
                default:
                    this.languageCode = codes[0];
                    this.scriptCode = codes[1];
                    this.countryCode = codes[2];
            }
        }
    };
    return DefaultLocale;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var IntlFormatter = /** @class */ (function () {
    function IntlFormatter() {
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} style
     * @param {?=} digits
     * @param {?=} currency
     * @param {?=} currencyDisplay
     * @return {?}
     */
    IntlFormatter.formatNumber = /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} style
     * @param {?=} digits
     * @param {?=} currency
     * @param {?=} currencyDisplay
     * @return {?}
     */
    function (value, defaultLocale, style, digits, currency, currencyDisplay) {
        if (!IntlAPI.hasNumberFormat())
            return currency ? value + " " + currency : value;
        value = typeof value === "string" && !isNaN(+value - parseFloat(value)) ? +value : value;
        return IntlFormatter.numberFormatter(value, defaultLocale, style, digits, currency, currencyDisplay);
    };
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} format
     * @param {?=} timezone
     * @return {?}
     */
    IntlFormatter.formatDate = /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} format
     * @param {?=} timezone
     * @return {?}
     */
    function (value, defaultLocale, format, timezone) {
        if (!IntlAPI.hasDateTimeFormat())
            return value;
        /** @type {?} */
        var date;
        if (typeof value === "string") {
            value = value.trim();
        }
        if (IntlFormatter.isDate(value)) {
            date = value;
        }
        else if (!isNaN(value - parseFloat(value))) {
            date = new Date(parseFloat(value));
        }
        else if (typeof value === "string" && /^(\d{4}-\d{1,2}-\d{1,2})$/.test(value)) {
            var _a = value.split('-').map(function (val) { return parseInt(val, 10); }), y = _a[0], m = _a[1], d = _a[2];
            date = new Date(y, m - 1, d);
        }
        else {
            date = new Date(value);
        }
        if (!IntlFormatter.isDate(date)) {
            /** @type {?} */
            var match = void 0;
            if ((typeof value === "string") && (match = value.match(ISO8601_DATE_REGEX))) {
                date = IntlFormatter.isoStringToDate(match);
            }
        }
        return IntlFormatter.dateTimeFormatter(date, defaultLocale, format, timezone);
    };
    /**
     * @param {?} num
     * @param {?} defaultLocale
     * @param {?} style
     * @param {?=} digits
     * @param {?=} currency
     * @param {?=} currencyDisplay
     * @return {?}
     */
    IntlFormatter.numberFormatter = /**
     * @param {?} num
     * @param {?} defaultLocale
     * @param {?} style
     * @param {?=} digits
     * @param {?=} currency
     * @param {?=} currencyDisplay
     * @return {?}
     */
    function (num, defaultLocale, style, digits, currency, currencyDisplay) {
        /** @type {?} */
        var options = {};
        if (digits) {
            if (typeof digits === "string") {
                /** @type {?} */
                var digitsOptions = formatDigitsAliases(digits);
                if (digitsOptions != null) {
                    options = digitsOptions;
                }
                else {
                    Logger.log('IntlFormatter', 'invalidNumberFormatAlias');
                }
            }
            else {
                options = digits;
            }
        }
        options.style = NumberFormatStyle[style].toLowerCase();
        if (style == NumberFormatStyle.Currency) {
            options.currency = currency;
            options.currencyDisplay = currencyDisplay;
        }
        return new Intl.NumberFormat(defaultLocale, options).format(num);
    };
    /**
     * @param {?} date
     * @param {?} defaultLocale
     * @param {?} format
     * @param {?=} timezone
     * @return {?}
     */
    IntlFormatter.dateTimeFormatter = /**
     * @param {?} date
     * @param {?} defaultLocale
     * @param {?} format
     * @param {?=} timezone
     * @return {?}
     */
    function (date, defaultLocale, format, timezone) {
        /** @type {?} */
        var options = {};
        if (format) {
            if (typeof format === "string") {
                /** @type {?} */
                var dateTimeOptions = FORMAT_ALIASES[format];
                if (dateTimeOptions) {
                    options = dateTimeOptions;
                }
                else {
                    Logger.log('IntlFormatter', 'invalidDateFormatAlias');
                }
            }
            else {
                options = format;
            }
        }
        options.timeZone = IntlAPI.hasTimezone() ? timezone : 'UTC';
        return new Intl.DateTimeFormat(defaultLocale, options).format(date).replace(/[\u200e\u200f]/g, "");
    };
    /**
     * @param {?} value
     * @return {?}
     */
    IntlFormatter.isDate = /**
     * @param {?} value
     * @return {?}
     */
    function (value) {
        return value instanceof Date && !isNaN(value.valueOf());
    };
    /**
     * @param {?} match
     * @return {?}
     */
    IntlFormatter.isoStringToDate = /**
     * @param {?} match
     * @return {?}
     */
    function (match) {
        /** @type {?} */
        var date = new Date(0);
        /** @type {?} */
        var tzHour = 0;
        /** @type {?} */
        var tzMin = 0;
        /** @type {?} */
        var dateSetter = match[8] ? date.setUTCFullYear : date.setFullYear;
        /** @type {?} */
        var timeSetter = match[8] ? date.setUTCHours : date.setHours;
        if (match[9]) {
            tzHour = +(match[9] + match[10]);
            tzMin = +(match[9] + match[11]);
        }
        dateSetter.call(date, +(match[1]), +(match[2]) - 1, +(match[3]));
        /** @type {?} */
        var h = +(match[4] || '0') - tzHour;
        /** @type {?} */
        var m = +(match[5] || '0') - tzMin;
        /** @type {?} */
        var s = +(match[6] || '0');
        /** @type {?} */
        var ms = Math.round(parseFloat('0.' + (match[7] || 0)) * 1000);
        timeSetter.call(date, h, m, s, ms);
        return date;
    };
    return IntlFormatter;
}());
/**
 * @param {?} digits
 * @return {?}
 */
function formatDigitsAliases(digits) {
    /** @type {?} */
    var parts = digits.match(NUMBER_FORMAT_REGEXP);
    if (parts == null)
        return null;
    /** @type {?} */
    var digitsOptions = {};
    if (parts[1] != null) {
        digitsOptions.minimumIntegerDigits = parseInt(parts[1]);
    }
    if (parts[3] != null) {
        digitsOptions.minimumFractionDigits = parseInt(parts[3]);
    }
    if (parts[5] != null) {
        digitsOptions.maximumFractionDigits = parseInt(parts[5]);
    }
    return digitsOptions;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Manages language, default locale, currency & timezone.
 */
var LocaleService = /** @class */ (function () {
    function LocaleService(configuration, storage) {
        this.configuration = configuration;
        this.storage = storage;
        /**
         * Fired when the language changes. Returns the language code.
         */
        this.languageCodeChanged = new Subject();
        /**
         * Fired when the default locale changes. Returns the default locale.
         */
        this.defaultLocaleChanged = new Subject();
        /**
         * Fired when the currency changes. Returns the currency code.
         */
        this.currencyCodeChanged = new Subject();
        /**
         * Fired when the timezone changes. Returns the timezone.
         */
        this.timezoneChanged = new Subject();
        this.defaultLocale = new DefaultLocale$1();
    }
    /**
     * @return {?}
     */
    LocaleService.prototype.getConfiguration = /**
     * @return {?}
     */
    function () {
        return this.configuration.locale;
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.init = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.initLanguage()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.initDefaultLocale()];
                    case 2:
                        _a.sent();
                        return [4 /*yield*/, this.initCurrency()];
                    case 3:
                        _a.sent();
                        return [4 /*yield*/, this.initTimezone()];
                    case 4:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getBrowserLanguage = /**
     * @return {?}
     */
    function () {
        /** @type {?} */
        var browserLanguage = null;
        if (typeof navigator !== "undefined" && navigator.language) {
            browserLanguage = navigator.language;
        }
        if (browserLanguage != null) {
            browserLanguage = browserLanguage.split("-")[0];
        }
        return browserLanguage;
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getAvailableLanguages = /**
     * @return {?}
     */
    function () {
        /** @type {?} */
        var languages = [];
        if (this.configuration.locale.languages) {
            languages = this.configuration.locale.languages.map(function (language) { return language.code; });
        }
        return languages;
    };
    /**
     * @param {?=} languageCode
     * @return {?}
     */
    LocaleService.prototype.getLanguageDirection = /**
     * @param {?=} languageCode
     * @return {?}
     */
    function (languageCode) {
        if (languageCode === void 0) { languageCode = this.defaultLocale.languageCode; }
        /** @type {?} */
        var matchedLanguage = this.matchLanguage(languageCode);
        return matchedLanguage ? matchedLanguage.dir : "";
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getCurrentLanguage = /**
     * @return {?}
     */
    function () {
        return this.defaultLocale.languageCode;
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getCurrentCountry = /**
     * @return {?}
     */
    function () {
        return this.defaultLocale.countryCode || "";
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getCurrentScript = /**
     * @return {?}
     */
    function () {
        return this.defaultLocale.scriptCode || "";
    };
    /**
     * Returns the well formatted locale as {languageCode}[-scriptCode][-countryCode]
     */
    /**
     * Returns the well formatted locale as {languageCode}[-scriptCode][-countryCode]
     * @return {?}
     */
    LocaleService.prototype.getCurrentLocale = /**
     * Returns the well formatted locale as {languageCode}[-scriptCode][-countryCode]
     * @return {?}
     */
    function () {
        /** @type {?} */
        var locale = this.defaultLocale.languageCode;
        locale += !!this.defaultLocale.scriptCode ? "-" + this.defaultLocale.scriptCode : "";
        locale += !!this.defaultLocale.countryCode ? "-" + this.defaultLocale.countryCode : "";
        return locale;
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getCurrentNumberingSystem = /**
     * @return {?}
     */
    function () {
        return this.defaultLocale.numberingSystem || "";
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getCurrentCalendar = /**
     * @return {?}
     */
    function () {
        return this.defaultLocale.calendar || "";
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getDefaultLocale = /**
     * @return {?}
     */
    function () {
        return this.defaultLocale.value;
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getCurrentCurrency = /**
     * @return {?}
     */
    function () {
        return this.currencyCode;
    };
    /**
     * @param {?} currencyDisplay
     * @param {?=} defaultLocale
     * @param {?=} currency
     * @return {?}
     */
    LocaleService.prototype.getCurrencySymbol = /**
     * @param {?} currencyDisplay
     * @param {?=} defaultLocale
     * @param {?=} currency
     * @return {?}
     */
    function (currencyDisplay, defaultLocale, currency) {
        /** @type {?} */
        var currencySymbol = this.currencyCode;
        if (IntlAPI.hasNumberFormat()) {
            /** @type {?} */
            var localeZero = this.formatDecimal(0, '1.0-0', defaultLocale);
            /** @type {?} */
            var localeValue = this.formatCurrency(0, '1.0-0', currencyDisplay, defaultLocale, currency);
            currencySymbol = localeValue.replace(localeZero, "");
            currencySymbol = currencySymbol.trim();
        }
        return currencySymbol;
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.getCurrentTimezone = /**
     * @return {?}
     */
    function () {
        return this.timezone;
    };
    /**
     * @param {?} languageCode
     * @return {?}
     */
    LocaleService.prototype.setCurrentLanguage = /**
     * @param {?} languageCode
     * @return {?}
     */
    function (languageCode) {
        if (this.defaultLocale.languageCode != languageCode) {
            this.rollbackLanguageCode = this.defaultLocale.languageCode;
            this.defaultLocale.build(languageCode);
            this.releaseLanguage();
        }
    };
    /**
     * @param {?} languageCode
     * @param {?=} countryCode
     * @param {?=} scriptCode
     * @param {?=} numberingSystem
     * @param {?=} calendar
     * @return {?}
     */
    LocaleService.prototype.setDefaultLocale = /**
     * @param {?} languageCode
     * @param {?=} countryCode
     * @param {?=} scriptCode
     * @param {?=} numberingSystem
     * @param {?=} calendar
     * @return {?}
     */
    function (languageCode, countryCode, scriptCode, numberingSystem, calendar) {
        if (this.defaultLocale.languageCode != languageCode ||
            this.defaultLocale.countryCode != countryCode ||
            this.defaultLocale.scriptCode != scriptCode ||
            this.defaultLocale.numberingSystem != numberingSystem ||
            this.defaultLocale.calendar != calendar) {
            this.rollbackDefaultLocale = this.defaultLocale.value;
            this.defaultLocale.build(languageCode, countryCode, scriptCode, numberingSystem, calendar);
            this.releaseDefaultLocale();
        }
    };
    /**
     * @param {?} currencyCode
     * @return {?}
     */
    LocaleService.prototype.setCurrentCurrency = /**
     * @param {?} currencyCode
     * @return {?}
     */
    function (currencyCode) {
        if (this.currencyCode != currencyCode) {
            this.rollbackCurrencyCode = this.currencyCode;
            this.currencyCode = currencyCode;
            this.releaseCurrency();
        }
    };
    /**
     * @param {?} zoneName
     * @return {?}
     */
    LocaleService.prototype.setCurrentTimezone = /**
     * @param {?} zoneName
     * @return {?}
     */
    function (zoneName) {
        if (this.timezone != zoneName) {
            this.rollbackTimezone = this.timezone;
            this.timezone = zoneName;
            this.releaseTimezone();
        }
    };
    /**
     * Formats a date according to default locale.
     * @param value A Date, a number (milliseconds since UTC epoch) or an ISO string
     * @param format An alias or a DateTimeOptions object of the format. Default is 'mediumDate'
     * @param defaultLocale The default locale to use. Default is the current locale
     * @param timezone The time zone name. Default is the current timezone
     */
    /**
     * Formats a date according to default locale.
     * @param {?} value A Date, a number (milliseconds since UTC epoch) or an ISO string
     * @param {?=} format An alias or a DateTimeOptions object of the format. Default is 'mediumDate'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @param {?=} timezone The time zone name. Default is the current timezone
     * @return {?}
     */
    LocaleService.prototype.formatDate = /**
     * Formats a date according to default locale.
     * @param {?} value A Date, a number (milliseconds since UTC epoch) or an ISO string
     * @param {?=} format An alias or a DateTimeOptions object of the format. Default is 'mediumDate'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @param {?=} timezone The time zone name. Default is the current timezone
     * @return {?}
     */
    function (value, format, defaultLocale, timezone) {
        return IntlFormatter.formatDate(value, defaultLocale || this.defaultLocale.value, format || 'mediumDate', timezone || this.timezone);
    };
    /**
     * Formats a decimal number according to default locale.
     * @param value The number to be formatted
     * @param digits An alias or a DigitsOptions object of the format
     * @param defaultLocale The default locale to use. Default is the current locale
     */
    /**
     * Formats a decimal number according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    LocaleService.prototype.formatDecimal = /**
     * Formats a decimal number according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    function (value, digits, defaultLocale) {
        return IntlFormatter.formatNumber(value, defaultLocale || this.defaultLocale.value, NumberFormatStyle.Decimal, digits);
    };
    /**
     * Formats a number as a percentage according to default locale.
     * @param value The number to be formatted
     * @param digits An alias or a DigitsOptions object of the format
     * @param defaultLocale The default locale to use. Default is the current locale
     */
    /**
     * Formats a number as a percentage according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    LocaleService.prototype.formatPercent = /**
     * Formats a number as a percentage according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    function (value, digits, defaultLocale) {
        return IntlFormatter.formatNumber(value, defaultLocale || this.defaultLocale.value, NumberFormatStyle.Percent, digits);
    };
    /**
     * Formats a number as a currency according to default locale.
     * @param value The number to be formatted
     * @param digits An alias or a DigitsOptions object of the format
     * @param currencyDisplay The format for the currency. Possible values are 'code', 'symbol', 'name'. Default is 'symbol'
     * @param defaultLocale The default locale to use. Default is the current locale
     * @param currency The currency to use. Default is the current currency
     */
    /**
     * Formats a number as a currency according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} currencyDisplay The format for the currency. Possible values are 'code', 'symbol', 'name'. Default is 'symbol'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @param {?=} currency The currency to use. Default is the current currency
     * @return {?}
     */
    LocaleService.prototype.formatCurrency = /**
     * Formats a number as a currency according to default locale.
     * @param {?} value The number to be formatted
     * @param {?=} digits An alias or a DigitsOptions object of the format
     * @param {?=} currencyDisplay The format for the currency. Possible values are 'code', 'symbol', 'name'. Default is 'symbol'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @param {?=} currency The currency to use. Default is the current currency
     * @return {?}
     */
    function (value, digits, currencyDisplay, defaultLocale, currency) {
        return IntlFormatter.formatNumber(value, defaultLocale || this.defaultLocale.value, NumberFormatStyle.Currency, digits, currency || this.currencyCode, currencyDisplay || 'symbol');
    };
    /**
     * @param {?} codes
     * @return {?}
     */
    LocaleService.prototype.composeLocale = /**
     * @param {?} codes
     * @return {?}
     */
    function (codes) {
        /** @type {?} */
        var locale = "";
        if (this.defaultLocale.languageCode) {
            for (var _i = 0, codes_1 = codes; _i < codes_1.length; _i++) {
                var code = codes_1[_i];
                switch (code) {
                    case ISOCode.Script:
                        locale += "-" + this.defaultLocale.scriptCode;
                        break;
                    case ISOCode.Country:
                        locale += "-" + this.defaultLocale.countryCode;
                        break;
                    default:
                        locale += this.defaultLocale.languageCode;
                }
            }
        }
        return locale;
    };
    /**
     * Rollbacks to previous language, default locale, currency & timezone.
     */
    /**
     * Rollbacks to previous language, default locale, currency & timezone.
     * @return {?}
     */
    LocaleService.prototype.rollback = /**
     * Rollbacks to previous language, default locale, currency & timezone.
     * @return {?}
     */
    function () {
        if (this.rollbackLanguageCode && this.rollbackLanguageCode != this.defaultLocale.languageCode) {
            this.defaultLocale.value = this.rollbackLanguageCode;
            this.releaseLanguage();
        }
        if (this.rollbackDefaultLocale && this.rollbackDefaultLocale != this.defaultLocale.value) {
            this.defaultLocale.value = this.rollbackDefaultLocale;
            this.releaseDefaultLocale();
        }
        if (this.rollbackCurrencyCode && this.rollbackCurrencyCode != this.currencyCode) {
            this.currencyCode = this.rollbackCurrencyCode;
            this.releaseCurrency();
        }
        if (this.rollbackTimezone && this.rollbackTimezone != this.timezone) {
            this.timezone = this.rollbackTimezone;
            this.releaseTimezone();
        }
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.initLanguage = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var defaultLocale, browserLanguage, matchedLanguage;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.configuration.locale.language) return [3 /*break*/, 2];
                        if (!!this.defaultLocale.languageCode) return [3 /*break*/, 2];
                        // Tries to get the language from the storage.
                        return [4 /*yield*/, this.storage.read("defaultLocale")];
                    case 1:
                        defaultLocale = _a.sent();
                        if (!!defaultLocale) {
                            this.defaultLocale.value = defaultLocale;
                        }
                        else {
                            // Tries to get the language from the browser.
                            browserLanguage = this.getBrowserLanguage();
                            matchedLanguage = this.matchLanguage(browserLanguage);
                            if (!!browserLanguage && matchedLanguage) {
                                this.defaultLocale.build(browserLanguage);
                            }
                            else {
                                // Uses the language set in the configuration.
                                this.defaultLocale.build(this.configuration.locale.language);
                            }
                            this.storage.write("defaultLocale", this.defaultLocale.value);
                        }
                        this.rollbackLanguageCode = this.defaultLocale.languageCode;
                        this.sendLanguageEvents();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.initDefaultLocale = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var defaultLocale;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.configuration.locale.defaultLocale) return [3 /*break*/, 2];
                        if (!!this.defaultLocale.value) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.storage.read("defaultLocale")];
                    case 1:
                        defaultLocale = _a.sent();
                        if (!!defaultLocale) {
                            this.defaultLocale.value = defaultLocale;
                        }
                        else {
                            this.defaultLocale.build(this.configuration.locale.defaultLocale.languageCode, this.configuration.locale.defaultLocale.countryCode, this.configuration.locale.defaultLocale.scriptCode, this.configuration.locale.defaultLocale.numberingSystem, this.configuration.locale.defaultLocale.calendar);
                            this.storage.write("defaultLocale", this.defaultLocale.value);
                        }
                        this.rollbackDefaultLocale = this.defaultLocale.value;
                        this.sendDefaultLocaleEvents();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.initCurrency = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var currencyCode;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.configuration.locale.currency) return [3 /*break*/, 2];
                        if (!!this.currencyCode) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.storage.read("currency")];
                    case 1:
                        currencyCode = _a.sent();
                        if (!!currencyCode) {
                            this.currencyCode = currencyCode;
                        }
                        else {
                            this.currencyCode = this.configuration.locale.currency;
                            this.storage.write("currency", this.currencyCode);
                        }
                        this.rollbackCurrencyCode = this.currencyCode;
                        this.sendCurrencyEvents();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.initTimezone = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var zoneName;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (!this.configuration.locale.timezone) return [3 /*break*/, 2];
                        if (!!this.timezone) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.storage.read("timezone")];
                    case 1:
                        zoneName = _a.sent();
                        if (!!zoneName) {
                            this.timezone = zoneName;
                        }
                        else {
                            this.timezone = this.configuration.locale.timezone;
                            this.storage.write("timezone", this.timezone);
                        }
                        this.rollbackTimezone = this.timezone;
                        this.sendTimezoneEvents();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * @param {?} languageCode
     * @return {?}
     */
    LocaleService.prototype.matchLanguage = /**
     * @param {?} languageCode
     * @return {?}
     */
    function (languageCode) {
        /** @type {?} */
        var matchedLanguage;
        if (this.configuration.locale.languages && languageCode != null) {
            matchedLanguage = this.configuration.locale.languages.find(function (language) { return language.code == languageCode; });
        }
        return matchedLanguage;
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.releaseLanguage = /**
     * @return {?}
     */
    function () {
        this.storage.write("defaultLocale", this.defaultLocale.value);
        this.sendLanguageEvents();
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.releaseDefaultLocale = /**
     * @return {?}
     */
    function () {
        this.storage.write("defaultLocale", this.defaultLocale.value);
        this.sendDefaultLocaleEvents();
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.releaseCurrency = /**
     * @return {?}
     */
    function () {
        this.storage.write("currency", this.currencyCode);
        this.sendCurrencyEvents();
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.releaseTimezone = /**
     * @return {?}
     */
    function () {
        this.storage.write("timezone", this.timezone);
        this.sendTimezoneEvents();
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.sendLanguageEvents = /**
     * @return {?}
     */
    function () {
        this.languageCodeChanged.next(this.defaultLocale.languageCode);
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.sendDefaultLocaleEvents = /**
     * @return {?}
     */
    function () {
        this.defaultLocaleChanged.next(this.defaultLocale.value);
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.sendCurrencyEvents = /**
     * @return {?}
     */
    function () {
        this.currencyCodeChanged.next(this.currencyCode);
    };
    /**
     * @return {?}
     */
    LocaleService.prototype.sendTimezoneEvents = /**
     * @return {?}
     */
    function () {
        this.timezoneChanged.next(this.timezone);
    };
    LocaleService.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    LocaleService.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
        { type: LocaleStorage }
    ]; };
    return LocaleService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var LocalizedRouting = /** @class */ (function () {
    function LocalizedRouting(configuration, locale) {
        this.configuration = configuration;
        this.locale = locale;
    }
    Object.defineProperty(LocalizedRouting.prototype, "router", {
        get: /**
         * @return {?}
         */
        function () {
            return InjectorRef.get(Router);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(LocalizedRouting.prototype, "location", {
        get: /**
         * @return {?}
         */
        function () {
            return InjectorRef.get(Location);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    LocalizedRouting.prototype.init = /**
     * @return {?}
     */
    function () {
        var _this = this;
        if (this.configuration.localizedRouting.format) {
            // Parses the url to find the locale when the app starts.
            /** @type {?} */
            var path = this.location.path(true);
            this.parsePath(path);
            // Parses the url to find the locale when a navigation starts.
            this.router.events.pipe(filter(function (event) { return event instanceof NavigationStart; })).subscribe(function (data) {
                _this.redirectToPath(data.url);
            });
            // Replaces url when a navigation ends.
            this.router.events.pipe(filter(function (event) { return event instanceof NavigationEnd; })).subscribe(function (data) {
                /** @type {?} */
                var url = (!!data.url && data.url != "/" && data.url == data.urlAfterRedirects) ?
                    data.url :
                    data.urlAfterRedirects;
                _this.replacePath(_this.locale.composeLocale((/** @type {?} */ (_this.configuration.localizedRouting.format))), url);
            });
            // Replaces url when locale changes.
            this.locale.languageCodeChanged.subscribe(function () {
                _this.replacePath(_this.locale.composeLocale((/** @type {?} */ (_this.configuration.localizedRouting.format))));
            });
            this.locale.defaultLocaleChanged.subscribe(function () {
                _this.replacePath(_this.locale.composeLocale((/** @type {?} */ (_this.configuration.localizedRouting.format))));
            });
        }
    };
    /**
     * Parses path to find the locale.
     * @param path The path to be parsed
     */
    /**
     * Parses path to find the locale.
     * @param {?=} path The path to be parsed
     * @return {?}
     */
    LocalizedRouting.prototype.parsePath = /**
     * Parses path to find the locale.
     * @param {?=} path The path to be parsed
     * @return {?}
     */
    function (path) {
        if (!path)
            return;
        /** @type {?} */
        var segment = this.getLocalizedSegment(path);
        if (segment != null) {
            /** @type {?} */
            var locale = (/** @type {?} */ (segment)).replace(/\//gi, "");
            /** @type {?} */
            var localeCodes = this.splitLocale(locale, (/** @type {?} */ (this.configuration.localizedRouting.format)));
            // Unrecognized segment.
            if (this.configuration.localizedRouting.schema) {
                if (!this.compareLocale(localeCodes, {
                    languageCode: localeCodes.languageCode,
                    scriptCode: this.getSchema(ISOCode.Script, localeCodes),
                    countryCode: this.getSchema(ISOCode.Country, localeCodes)
                }))
                    return;
            }
            if (this.configuration.locale.language) {
                this.locale.setCurrentLanguage(localeCodes.languageCode);
            }
            if (this.configuration.locale.defaultLocale) {
                this.locale.setDefaultLocale(localeCodes.languageCode, localeCodes.countryCode || this.getSchema(ISOCode.Country, localeCodes), localeCodes.scriptCode || this.getSchema(ISOCode.Script, localeCodes), this.getSchema(ExtraCode.NumberingSystem, localeCodes), this.getSchema(ExtraCode.Calendar, localeCodes));
            }
            if (this.configuration.locale.currency) {
                /** @type {?} */
                var currency = this.getSchema(ExtraCode.Currency, localeCodes);
                if (currency) {
                    this.locale.setCurrentCurrency(currency);
                }
            }
            if (this.configuration.locale.timezone) {
                /** @type {?} */
                var timezone = this.getSchema(ExtraCode.Timezone, localeCodes);
                if (timezone) {
                    this.locale.setCurrentTimezone(timezone);
                }
            }
        }
    };
    /**
     * Removes the locale from the path and navigates without pushing a new state into history.
     * @param path Localized path
     */
    /**
     * Removes the locale from the path and navigates without pushing a new state into history.
     * @param {?} path Localized path
     * @return {?}
     */
    LocalizedRouting.prototype.redirectToPath = /**
     * Removes the locale from the path and navigates without pushing a new state into history.
     * @param {?} path Localized path
     * @return {?}
     */
    function (path) {
        /** @type {?} */
        var segment = this.getLocalizedSegment(path);
        if (segment != null) {
            /** @type {?} */
            var url = path.replace(segment, "/");
            // navigateByUrl keeps the query params.
            this.router.navigateByUrl(url, { skipLocationChange: true });
        }
    };
    /**
     * Replaces the path with the locale without pushing a new state into history.
     * @param locale The current locale
     * @param path The path to be replaced
     */
    /**
     * Replaces the path with the locale without pushing a new state into history.
     * @param {?} locale The current locale
     * @param {?=} path The path to be replaced
     * @return {?}
     */
    LocalizedRouting.prototype.replacePath = /**
     * Replaces the path with the locale without pushing a new state into history.
     * @param {?} locale The current locale
     * @param {?=} path The path to be replaced
     * @return {?}
     */
    function (locale, path) {
        if (path) {
            if (!this.isDefaultRouting()) {
                this.location.replaceState(this.getLocalizedPath(locale, path));
            }
        }
        else {
            path = this.location.path(true);
            // Parses the path to find the locale.
            /** @type {?} */
            var segment = this.getLocalizedSegment(path);
            if (segment != null) {
                // Removes the locale from the path.
                path = path.replace(segment, "/");
                if (this.isDefaultRouting()) {
                    this.location.replaceState(path);
                }
            }
            if (!this.isDefaultRouting()) {
                this.location.replaceState(this.getLocalizedPath(locale, path));
            }
        }
    };
    /**
     * @param {?} path
     * @return {?}
     */
    LocalizedRouting.prototype.getLocalizedSegment = /**
     * @param {?} path
     * @return {?}
     */
    function (path) {
        for (var _i = 0, _a = this.locale.getAvailableLanguages(); _i < _a.length; _i++) {
            var lang = _a[_i];
            /** @type {?} */
            var regex = new RegExp("(^/" + lang + "/)|(^/" + lang + "$)|(^/" + lang + "-.*?/)|(^/" + lang + "-.*?$)");
            /** @type {?} */
            var segments = path.match(regex);
            if (segments != null) {
                return segments[0];
            }
        }
        return null;
    };
    /**
     * @param {?} locale
     * @param {?} codes
     * @return {?}
     */
    LocalizedRouting.prototype.splitLocale = /**
     * @param {?} locale
     * @param {?} codes
     * @return {?}
     */
    function (locale, codes) {
        /** @type {?} */
        var values = locale.split("-");
        /** @type {?} */
        var localeCodes = { languageCode: "" };
        if (codes.length > 0) {
            for (var i = 0; i < codes.length; i++) {
                localeCodes[codes[i]] = values[i] || undefined;
            }
        }
        return localeCodes;
    };
    /**
     * @param {?} locale1
     * @param {?} locale2
     * @return {?}
     */
    LocalizedRouting.prototype.compareLocale = /**
     * @param {?} locale1
     * @param {?} locale2
     * @return {?}
     */
    function (locale1, locale2) {
        return locale1.languageCode == locale2.languageCode &&
            (!!locale1.scriptCode ?
                locale1.scriptCode == locale2.scriptCode : true) &&
            (!!locale1.countryCode ?
                locale1.countryCode == locale2.countryCode : true);
    };
    /**
     * @param {?} code
     * @param {?} locale
     * @return {?}
     */
    LocalizedRouting.prototype.getSchema = /**
     * @param {?} code
     * @param {?} locale
     * @return {?}
     */
    function (code, locale) {
        var _this = this;
        if (!this.configuration.localizedRouting.schema)
            return undefined;
        /** @type {?} */
        var schema = this.configuration.localizedRouting.schema
            .find((function (s) { return _this.compareLocale(locale, s); }));
        return schema ? schema[code] : undefined;
    };
    /**
     * @return {?}
     */
    LocalizedRouting.prototype.isDefaultRouting = /**
     * @return {?}
     */
    function () {
        if (!this.configuration.localizedRouting.defaultRouting)
            return false;
        if (this.configuration.locale.language) {
            return this.locale.getCurrentLanguage() == this.configuration.locale.language;
        }
        if (this.configuration.locale.defaultLocale) {
            return this.compareLocale({
                languageCode: this.locale.getCurrentLanguage(),
                scriptCode: this.locale.getCurrentScript(),
                countryCode: this.locale.getCurrentCountry()
            }, this.configuration.locale.defaultLocale);
        }
        return false;
    };
    /**
     * @param {?} locale
     * @param {?} path
     * @return {?}
     */
    LocalizedRouting.prototype.getLocalizedPath = /**
     * @param {?} locale
     * @param {?} path
     * @return {?}
     */
    function (locale, path) {
        return Location.stripTrailingSlash("/" + locale + path);
    };
    LocalizedRouting.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    LocalizedRouting.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
        { type: LocaleService }
    ]; };
    return LocalizedRouting;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Implement this class-interface to create a custom provider for translation data.
 * @abstract
 */
var TranslationProvider = /** @class */ (function () {
    function TranslationProvider() {
    }
    TranslationProvider.decorators = [
        { type: Injectable }
    ];
    return TranslationProvider;
}());
var L10nTranslationProvider = /** @class */ (function () {
    function L10nTranslationProvider(http, configuration, caching) {
        this.http = http;
        this.configuration = configuration;
        this.caching = caching;
        this.headers = new HttpHeaders({ 'Content-Type': 'application/json' });
        this.options = {};
    }
    /**
     * @param {?} language
     * @param {?} args
     * @return {?}
     */
    L10nTranslationProvider.prototype.getTranslation = /**
     * @param {?} language
     * @param {?} args
     * @return {?}
     */
    function (language, args) {
        this.setOptions();
        /** @type {?} */
        var url = "";
        switch (args.type) {
            case ProviderType.WebAPI:
                url += args.path + language;
                break;
            default:
                url += args.prefix + language + ".json";
        }
        return this.getRequest(url);
    };
    /**
     * @return {?}
     */
    L10nTranslationProvider.prototype.setOptions = /**
     * @return {?}
     */
    function () {
        this.options = {
            headers: this.headers,
            params: this.configuration.translation.version ? new HttpParams().set('ver', this.configuration.translation.version) : undefined
        };
    };
    /**
     * @param {?} url
     * @return {?}
     */
    L10nTranslationProvider.prototype.getRequest = /**
     * @param {?} url
     * @return {?}
     */
    function (url) {
        /** @type {?} */
        var request;
        if (this.configuration.translation.timeout) {
            request = this.http.get(url, this.options).pipe(timeout(this.configuration.translation.timeout));
        }
        else {
            request = this.http.get(url, this.options);
        }
        if (this.configuration.translation.caching) {
            return this.caching.read(url, request);
        }
        return request;
    };
    L10nTranslationProvider.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    L10nTranslationProvider.ctorParameters = function () { return [
        { type: HttpClient, decorators: [{ type: Optional }] },
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
        { type: Caching }
    ]; };
    return L10nTranslationProvider;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Implement this class-interface to create a custom handler for translated values.
 * @abstract
 */
var TranslationHandler = /** @class */ (function () {
    function TranslationHandler() {
    }
    TranslationHandler.decorators = [
        { type: Injectable }
    ];
    return TranslationHandler;
}());
var L10nTranslationHandler = /** @class */ (function () {
    function L10nTranslationHandler(configuration) {
        this.configuration = configuration;
    }
    /**
     * @param {?} path
     * @param {?} key
     * @param {?} value
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    L10nTranslationHandler.prototype.parseValue = /**
     * @param {?} path
     * @param {?} key
     * @param {?} value
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    function (path, key, value, args, lang) {
        if (value == null) {
            return this.handleMissingValue(path);
        }
        else if (args) {
            return this.handleArgs(value, args);
        }
        return value;
    };
    /**
     * @param {?} path
     * @return {?}
     */
    L10nTranslationHandler.prototype.handleMissingValue = /**
     * @param {?} path
     * @return {?}
     */
    function (path) {
        if (this.configuration.translation.missingValue != null) {
            return typeof this.configuration.translation.missingValue === "function"
                ? this.configuration.translation.missingValue(path)
                : this.configuration.translation.missingValue;
        }
        // The same path is returned.
        return path;
    };
    /**
     * @param {?} value
     * @param {?} args
     * @return {?}
     */
    L10nTranslationHandler.prototype.handleArgs = /**
     * @param {?} value
     * @param {?} args
     * @return {?}
     */
    function (value, args) {
        /** @type {?} */
        var TEMPLATE_REGEXP = /{{\s?([^{}\s]*)\s?}}/g;
        return value.replace(TEMPLATE_REGEXP, function (substring, parsedKey) {
            /** @type {?} */
            var replacer = (/** @type {?} */ (args[parsedKey]));
            return typeof replacer !== "undefined" ? replacer : substring;
        });
    };
    L10nTranslationHandler.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    L10nTranslationHandler.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] }
    ]; };
    return L10nTranslationHandler;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} item
 * @return {?}
 */
function isObject(item) {
    return (typeof item === "object" && !Array.isArray(item));
}
/**
 * @param {?} target
 * @param {?} source
 * @return {?}
 */
function mergeDeep(target, source) {
    /** @type {?} */
    var output = Object.assign({}, target);
    if (isObject(target) && isObject(source)) {
        Object.keys(source).forEach(function (key) {
            var _a, _b;
            if (isObject(source[key])) {
                if (!(key in target)) {
                    Object.assign(output, (_a = {}, _a[key] = source[key], _a));
                }
                else {
                    output[key] = mergeDeep(target[key], source[key]);
                }
            }
            else {
                Object.assign(output, (_b = {}, _b[key] = source[key], _b));
            }
        });
    }
    return output;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Manages the translation data.
 */
var TranslationService = /** @class */ (function () {
    function TranslationService(configuration, locale, translationProvider, translationHandler, injector) {
        this.configuration = configuration;
        this.locale = locale;
        this.translationProvider = translationProvider;
        this.translationHandler = translationHandler;
        this.injector = injector;
        /**
         * Fired when the translation data could not been loaded. Returns the error.
         */
        this.translationError = new Subject();
        this.translation = new BehaviorSubject('');
        /**
         * The translation data: {language: {key: value}}.
         */
        this.translationData = {};
        this.injector.translations.push(this);
    }
    /**
     * @return {?}
     */
    TranslationService.prototype.getConfiguration = /**
     * @return {?}
     */
    function () {
        return this.configuration.translation;
    };
    /**
     * @return {?}
     */
    TranslationService.prototype.init = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var _this = this;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        // When the language or the default locale changes, loads translation data.
                        race(this.locale.languageCodeChanged, this.locale.defaultLocaleChanged).subscribe(function () {
                            _this.loadTranslation()
                                .catch(function (error) { return null; });
                        });
                        return [4 /*yield*/, this.loadTranslation()
                                .catch(function (error) { throw error; })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Forces the translation loading for the current language.
     */
    /**
     * Forces the translation loading for the current language.
     * @return {?}
     */
    TranslationService.prototype.loadTranslation = /**
     * Forces the translation loading for the current language.
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            var language;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this.configuration.translation.composedLanguage) {
                            language = this.locale.composeLocale(this.configuration.translation.composedLanguage);
                        }
                        else {
                            language = this.locale.getCurrentLanguage();
                        }
                        if (!language) return [3 /*break*/, 2];
                        this.translationData = {};
                        if (this.configuration.translation.translationData) {
                            this.getTranslation(language);
                        }
                        if (!this.configuration.translation.providers) return [3 /*break*/, 2];
                        return [4 /*yield*/, this.getTranslationAsync(language)
                                .toPromise()
                                .catch(function (error) { throw error; })];
                    case 1:
                        _a.sent();
                        _a.label = 2;
                    case 2: return [2 /*return*/];
                }
            });
        });
    };
    /**
     * Fired when the translation data has been loaded. Returns the translation language.
     */
    /**
     * Fired when the translation data has been loaded. Returns the translation language.
     * @return {?}
     */
    TranslationService.prototype.translationChanged = /**
     * Fired when the translation data has been loaded. Returns the translation language.
     * @return {?}
     */
    function () {
        return this.translation.asObservable();
    };
    /**
     * Fired when the latest 'translationChanged' is emitted. Returns the translation language.
     * Used when the reference to the service is not known, as in decorators.
     */
    /**
     * Fired when the latest 'translationChanged' is emitted. Returns the translation language.
     * Used when the reference to the service is not known, as in decorators.
     * @return {?}
     */
    TranslationService.prototype.latestTranslation = /**
     * Fired when the latest 'translationChanged' is emitted. Returns the translation language.
     * Used when the reference to the service is not known, as in decorators.
     * @return {?}
     */
    function () {
        /** @type {?} */
        var sequencesOfTranslation = [];
        for (var _i = 0, _a = this.injector.translations; _i < _a.length; _i++) {
            var translation = _a[_i];
            sequencesOfTranslation.push(translation.translationChanged());
        }
        return combineLatest(sequencesOfTranslation).pipe(filter(function (languages) {
            return languages.length == sequencesOfTranslation.length &&
                languages.every(function (lang, i, arr) { return lang == arr[0]; });
        }), map(function (languages) { return languages[0]; }));
    };
    /**
     * Translates a key or an array of keys.
     * @param keys The key or an array of keys to be translated
     * @param args Optional parameters contained in the key
     * @param lang The current language of the service is used by default
     * @return The translated value or an object: {key: value}
     */
    /**
     * Translates a key or an array of keys.
     * @param {?} keys The key or an array of keys to be translated
     * @param {?=} args Optional parameters contained in the key
     * @param {?=} lang The current language of the service is used by default
     * @return {?} The translated value or an object: {key: value}
     */
    TranslationService.prototype.translate = /**
     * Translates a key or an array of keys.
     * @param {?} keys The key or an array of keys to be translated
     * @param {?=} args Optional parameters contained in the key
     * @param {?=} lang The current language of the service is used by default
     * @return {?} The translated value or an object: {key: value}
     */
    function (keys, args, lang) {
        if (Array.isArray(keys)) {
            /** @type {?} */
            var data = {};
            for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
                var key = keys_1[_i];
                data[key] = this.translateKey(key, args, lang || this.translation.getValue());
            }
            return data;
        }
        return this.translateKey(keys, args, lang || this.translation.getValue());
    };
    /**
     * @param {?} keys
     * @param {?=} args
     * @param {?=} lang
     * @return {?}
     */
    TranslationService.prototype.translateAsync = /**
     * @param {?} keys
     * @param {?=} args
     * @param {?=} lang
     * @return {?}
     */
    function (keys, args, lang) {
        var _this = this;
        return Observable.create(function (observer) {
            /** @type {?} */
            var values = _this.translate(keys, args, lang);
            observer.next(values);
            observer.complete();
        });
    };
    /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    TranslationService.prototype.translateKey = /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    function (key, args, lang) {
        // I18n plural.
        if (this.configuration.translation.i18nPlural && /^\d+\b/.exec(key)) {
            return this.translateI18nPlural(key, args, lang);
        }
        return this.getValue(key, args, lang);
    };
    /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    TranslationService.prototype.getValue = /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    function (key, args, lang) {
        /** @type {?} */
        var path = key;
        /** @type {?} */
        var value = null;
        /** @type {?} */
        var translation = this.translationData[lang];
        if (translation) {
            // Composed key.
            if (this.configuration.translation.composedKeySeparator) {
                /** @type {?} */
                var sequences = key.split(this.configuration.translation.composedKeySeparator);
                key = (/** @type {?} */ (sequences.shift()));
                while (sequences.length > 0 && translation[key]) {
                    translation = translation[key];
                    key = (/** @type {?} */ (sequences.shift()));
                }
            }
            value = typeof translation[key] === "undefined" ?
                translation[this.configuration.translation.missingKey || ""] :
                translation[key];
        }
        return this.translationHandler.parseValue(path, key, value, args, lang);
    };
    /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    TranslationService.prototype.translateI18nPlural = /**
     * @param {?} key
     * @param {?} args
     * @param {?} lang
     * @return {?}
     */
    function (key, args, lang) {
        /** @type {?} */
        var keyText = key.replace(/^\d+\b/, "");
        keyText = keyText.trim();
        /** @type {?} */
        var keyNumber = parseFloat(key);
        if (!isNaN(keyNumber)) {
            key = key.replace(/^\d+/, this.locale.formatDecimal(keyNumber));
        }
        return key.replace(keyText, this.getValue(keyText, args, lang));
    };
    /**
     * @param {?} language
     * @return {?}
     */
    TranslationService.prototype.getTranslation = /**
     * @param {?} language
     * @return {?}
     */
    function (language) {
        /** @type {?} */
        var translations = (/** @type {?} */ (this.configuration.translation.translationData)).filter(function (value) { return value.languageCode == language; });
        for (var _i = 0, translations_1 = translations; _i < translations_1.length; _i++) {
            var translation = translations_1[_i];
            this.addData(translation.data, language);
        }
        if (!this.configuration.translation.providers) {
            this.releaseTranslation(language);
        }
    };
    /**
     * @param {?} language
     * @return {?}
     */
    TranslationService.prototype.getTranslationAsync = /**
     * @param {?} language
     * @return {?}
     */
    function (language) {
        var _this = this;
        return Observable.create(function (observer) {
            /** @type {?} */
            var sequencesOfOrderedTranslationData = [];
            /** @type {?} */
            var sequencesOfTranslationData = [];
            for (var _i = 0, _a = (/** @type {?} */ (_this.configuration.translation.providers)); _i < _a.length; _i++) {
                var provider = _a[_i];
                if (typeof provider.type !== "undefined" && provider.type == ProviderType.Fallback) {
                    /** @type {?} */
                    var fallbackLanguage = language;
                    if (provider.fallbackLanguage) {
                        fallbackLanguage = _this.locale.composeLocale(provider.fallbackLanguage);
                    }
                    sequencesOfOrderedTranslationData.push(_this.translationProvider.getTranslation(fallbackLanguage, provider));
                }
                else {
                    sequencesOfTranslationData.push(_this.translationProvider.getTranslation(language, provider));
                }
            }
            // Merges all the sequences into a single observable sequence.
            /** @type {?} */
            var mergedSequencesOfTranslationData = merge.apply(void 0, sequencesOfTranslationData);
            // Adds to ordered sequences.
            sequencesOfOrderedTranslationData.push(mergedSequencesOfTranslationData);
            concat.apply(void 0, sequencesOfOrderedTranslationData).subscribe(function (data) {
                _this.addData(data, language);
            }, function (error) {
                _this.handleError(error, language);
                observer.error(error);
                observer.complete();
            }, function () {
                _this.releaseTranslation(language);
                observer.next(null);
                observer.complete();
            });
        });
    };
    /**
     * @param {?} data
     * @param {?} language
     * @return {?}
     */
    TranslationService.prototype.addData = /**
     * @param {?} data
     * @param {?} language
     * @return {?}
     */
    function (data, language) {
        this.translationData[language] = typeof this.translationData[language] !== "undefined"
            ? mergeDeep(this.translationData[language], data)
            : data;
    };
    /**
     * @param {?} language
     * @return {?}
     */
    TranslationService.prototype.releaseTranslation = /**
     * @param {?} language
     * @return {?}
     */
    function (language) {
        this.translation.next(language);
    };
    /**
     * @param {?} error
     * @param {?} language
     * @return {?}
     */
    TranslationService.prototype.handleError = /**
     * @param {?} error
     * @param {?} language
     * @return {?}
     */
    function (error, language) {
        if (this.configuration.translation.rollbackOnError) {
            this.locale.rollback();
        }
        else {
            this.releaseTranslation(language);
        }
        this.translationError.next(error);
    };
    TranslationService.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    TranslationService.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
        { type: LocaleService },
        { type: TranslationProvider },
        { type: TranslationHandler },
        { type: InjectorRef }
    ]; };
    return TranslationService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Initializes the services.
 * @abstract
 */
var L10nLoader = /** @class */ (function () {
    function L10nLoader() {
    }
    L10nLoader.decorators = [
        { type: Injectable }
    ];
    return L10nLoader;
}());
var LocaleLoader = /** @class */ (function () {
    function LocaleLoader(locale, translation) {
        this.locale = locale;
        this.translation = translation;
    }
    /**
     * @return {?}
     */
    LocaleLoader.prototype.load = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.locale.init()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.translation.init()
                                .catch(function (error) { throw error; })];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    LocaleLoader.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    LocaleLoader.ctorParameters = function () { return [
        { type: LocaleService },
        { type: TranslationService }
    ]; };
    return LocaleLoader;
}());
var TranslationLoader = /** @class */ (function () {
    function TranslationLoader(translation) {
        this.translation = translation;
    }
    /**
     * @return {?}
     */
    TranslationLoader.prototype.load = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, this.translation.init()
                            .catch(function (error) { throw error; })];
                    case 1:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    TranslationLoader.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    TranslationLoader.ctorParameters = function () { return [
        { type: TranslationService }
    ]; };
    return TranslationLoader;
}());
var LocalizedRoutingLoader = /** @class */ (function () {
    function LocalizedRoutingLoader(localizedRouting, locale, translation) {
        this.localizedRouting = localizedRouting;
        this.locale = locale;
        this.translation = translation;
    }
    /**
     * @return {?}
     */
    LocalizedRoutingLoader.prototype.load = /**
     * @return {?}
     */
    function () {
        return __awaiter(this, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        this.localizedRouting.init();
                        return [4 /*yield*/, this.locale.init()];
                    case 1:
                        _a.sent();
                        return [4 /*yield*/, this.translation.init()
                                .catch(function (error) { throw error; })];
                    case 2:
                        _a.sent();
                        return [2 /*return*/];
                }
            });
        });
    };
    LocalizedRoutingLoader.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    LocalizedRoutingLoader.ctorParameters = function () { return [
        { type: LocalizedRouting },
        { type: LocaleService },
        { type: TranslationService }
    ]; };
    return LocalizedRoutingLoader;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} target
 * @return {?}
 */
function takeUntilDestroyed(target) {
    /** @type {?} */
    var destroy = new Subject();
    /** @type {?} */
    var targetNgOnDestroy = target.ngOnDestroy;
    if (typeof targetNgOnDestroy === "undefined") {
        Logger.log(target.constructor ? target.constructor.name : 'takeUntilDestroyed', 'missingOnDestroy');
    }
    /**
     * @this {?}
     * @return {?}
     */
    function ngOnDestroy() {
        destroy.next(true);
        destroy.complete();
        if (targetNgOnDestroy) {
            targetNgOnDestroy.apply(this);
        }
    }
    target.ngOnDestroy = ngOnDestroy;
    return takeUntil(destroy);
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides 'lang' to translate pipe.
 */
var Translation = /** @class */ (function () {
    function Translation(changeDetectorRef) {
        var _this = this;
        this.changeDetectorRef = changeDetectorRef;
        this.lang = '';
        /** @type {?} */
        var translation = InjectorRef.get(TranslationService);
        translation.latestTranslation().pipe(takeUntilDestroyed(this)).subscribe(function (language) {
            _this.lang = language;
            // OnPush Change Detection strategy.
            if (_this.changeDetectorRef) {
                _this.changeDetectorRef.markForCheck();
            }
        });
    }
    /**
     * @return {?}
     */
    Translation.prototype.ngOnDestroy = /**
     * @return {?}
     */
    function () { };
    Translation.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Translation.ctorParameters = function () { return [
        { type: ChangeDetectorRef }
    ]; };
    return Translation;
}());
/**
 * Provides 'lang' to translate pipe,
 * 'defaultLocale', 'currency', 'timezone' to l10nDate, l10nDecimal, l10nPercent & l10nCurrency pipes.
 */
var Localization = /** @class */ (function (_super) {
    __extends(Localization, _super);
    function Localization(changeDetectorRef) {
        var _this = _super.call(this) || this;
        _this.changeDetectorRef = changeDetectorRef;
        _this.defaultLocale = '';
        _this.currency = '';
        _this.timezone = '';
        /** @type {?} */
        var locale = InjectorRef.get(LocaleService);
        _this.defaultLocale = locale.getDefaultLocale();
        locale.defaultLocaleChanged.pipe(takeUntilDestroyed(_this)).subscribe(function (defaultLocale) {
            _this.defaultLocale = defaultLocale;
            if (_this.changeDetectorRef) {
                _this.changeDetectorRef.markForCheck();
            }
        });
        _this.currency = locale.getCurrentCurrency();
        locale.currencyCodeChanged.pipe(takeUntilDestroyed(_this)).subscribe(function (currency) {
            _this.currency = currency;
            if (_this.changeDetectorRef) {
                _this.changeDetectorRef.markForCheck();
            }
        });
        _this.timezone = locale.getCurrentTimezone();
        locale.timezoneChanged.pipe(takeUntilDestroyed(_this)).subscribe(function (zoneName) {
            _this.timezone = zoneName;
            if (_this.changeDetectorRef) {
                _this.changeDetectorRef.markForCheck();
            }
        });
        return _this;
    }
    Localization.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Localization.ctorParameters = function () { return [
        { type: ChangeDetectorRef }
    ]; };
    return Localization;
}(Translation));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides the methods for locale validation.
 */
var LocaleValidation = /** @class */ (function () {
    function LocaleValidation(locale) {
        this.locale = locale;
    }
    /**
     * Converts a string to a number according to default locale.
     * If the string cannot be converted to a number, returns NaN.
     * @param s The string to be parsed
     * @param digits An alias of the format. Default is '1.0-3'
     * @param defaultLocale The default locale to use. Default is the current locale
     */
    /**
     * Converts a string to a number according to default locale.
     * If the string cannot be converted to a number, returns NaN.
     * @param {?} s The string to be parsed
     * @param {?=} digits An alias of the format. Default is '1.0-3'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    LocaleValidation.prototype.parseNumber = /**
     * Converts a string to a number according to default locale.
     * If the string cannot be converted to a number, returns NaN.
     * @param {?} s The string to be parsed
     * @param {?=} digits An alias of the format. Default is '1.0-3'
     * @param {?=} defaultLocale The default locale to use. Default is the current locale
     * @return {?}
     */
    function (s, digits, defaultLocale) {
        if (s == "" || s == null)
            return null;
        this.decimalCode = this.getDecimalCode(defaultLocale);
        this.numberCodes = this.getNumberCodes(defaultLocale);
        if (!this.validateNumber(s, digits))
            return NaN;
        /** @type {?} */
        var value = "";
        /** @type {?} */
        var characters = s.split("");
        for (var _i = 0, characters_1 = characters; _i < characters_1.length; _i++) {
            var char = characters_1[_i];
            /** @type {?} */
            var charCode = this.toUnicode(char);
            /** @type {?} */
            var index = this.numberCodes.indexOf(charCode);
            if (index != -1) {
                value += index;
            }
            else if (charCode == this.decimalCode.minusSign) {
                value += "-";
            }
            else if (charCode == this.decimalCode.decimalSeparator) {
                value += ".";
            }
            else if (charCode == this.decimalCode.thousandSeparator) {
                continue;
            }
            else {
                return NaN;
            }
        }
        return parseFloat(value);
    };
    /**
     * @param {?} s
     * @param {?=} digits
     * @return {?}
     */
    LocaleValidation.prototype.validateNumber = /**
     * @param {?} s
     * @param {?=} digits
     * @return {?}
     */
    function (s, digits) {
        /** @type {?} */
        var options = {};
        if (digits) {
            /** @type {?} */
            var digitsOptions = formatDigitsAliases(digits);
            if (digitsOptions != null) {
                options = digitsOptions;
            }
            else {
                Logger.log('LocaleValidation', 'invalidNumberFormatAlias');
            }
        }
        /** @type {?} */
        var minInt = options.minimumIntegerDigits !== undefined ? options.minimumIntegerDigits : 1;
        /** @type {?} */
        var minFraction = options.minimumFractionDigits !== undefined ? options.minimumFractionDigits : 0;
        /** @type {?} */
        var maxFraction = options.maximumFractionDigits !== undefined ? options.maximumFractionDigits : 3;
        /** @type {?} */
        var minusSign = this.decimalCode.minusSign;
        /** @type {?} */
        var zero = this.numberCodes[0];
        /** @type {?} */
        var decimalSeparator = this.decimalCode.decimalSeparator;
        /** @type {?} */
        var thousandSeparator = this.decimalCode.thousandSeparator;
        /** @type {?} */
        var nine = this.numberCodes[9];
        // Pattern for 1.0-2 digits: /^-?[0-9]{1,}(\.[0-9]{0,2})?$/
        // Unicode pattern = "^\u002d?[\u0030-\u0039]{1,}(\\u002e[\u0030-\u0039]{0,2})?$"
        // Complete Pattern with thousand separator:
        // /^-?([0-9]{1,}|(?=(?:\,*[0-9]){1,}(\.|$))(?!0(?!\.|[0-9]))[0-9]{1,3}(\,[0-9]{3})*)(\.[0-9]{0,2})?$/
        // where:
        // (?=(?:\,*[0-9]){1,}(\.|$)) => Positive Lookahead to count the integer digits
        // (?!0(?!\.|[0-9])) => Negative Lookahead to avoid 0,1111.00
        // [0-9]{1,3}(\,[0-9]{3})* => Allows thousand separator
        /** @type {?} */
        var d = "[" + zero + "-" + nine + "]";
        /** @type {?} */
        var n = "{" + minInt + ",}";
        /** @type {?} */
        var nm = "{" + minFraction + "," + maxFraction + "}";
        /** @type {?} */
        var plainPattern = "" + d + n;
        // tslint:disable-next-line
        /** @type {?} */
        var thousandPattern = "(?=(?:\\" + thousandSeparator + "*" + d + ")" + n + "(\\" + decimalSeparator + "|$))(?!" + zero + "(?!\\" + decimalSeparator + "|" + d + "))" + d + "{1,3}(\\" + thousandSeparator + d + "{3})*";
        /** @type {?} */
        var pattern = "^" + minusSign + "?(" + plainPattern + "|" + thousandPattern + ")";
        if (minFraction > 0 && maxFraction > 0) {
            // Decimal separator is mandatory.
            pattern += "\\" + decimalSeparator + d + nm + "$";
        }
        else if (minFraction == 0 && maxFraction > 0) {
            // Decimal separator is optional.
            pattern += "(\\" + decimalSeparator + d + nm + ")?$";
        }
        else {
            // Integer number.
            pattern += "$";
        }
        pattern = this.toChar(pattern);
        /** @type {?} */
        var NUMBER_REGEXP = new RegExp(pattern);
        return NUMBER_REGEXP.test(s);
    };
    /**
     * @param {?=} defaultLocale
     * @return {?}
     */
    LocaleValidation.prototype.getDecimalCode = /**
     * @param {?=} defaultLocale
     * @return {?}
     */
    function (defaultLocale) {
        /** @type {?} */
        var decimalCode = {
            minusSign: this.toUnicode("-"),
            decimalSeparator: this.toUnicode("."),
            thousandSeparator: this.toUnicode(",")
        };
        if (IntlAPI.hasNumberFormat()) {
            /** @type {?} */
            var value = -1000.9;
            // Reference value.
            /** @type {?} */
            var localeValue = this.locale.formatDecimal(value, '1.1-1', defaultLocale);
            /** @type {?} */
            var unicodeChars = [];
            for (var i = 0; i <= 7; i++) {
                unicodeChars.push(this.toUnicode(localeValue.charAt(i)));
            }
            // Right to left:
            // checks Unicode characters 'RIGHT-TO-LEFT MARK' (U+200F) & 'Arabic Letter Mark' (U+061C),
            // or the reverse order.
            // Left to right:
            // checks Unicode character 'LEFT-TO-RIGHT MARK' (U+200E).
            if (unicodeChars[0] == "\\u200F" || unicodeChars[0] == "\\u061C") {
                decimalCode = {
                    minusSign: unicodeChars[1],
                    decimalSeparator: unicodeChars[7],
                    thousandSeparator: unicodeChars[3]
                };
            }
            else if (unicodeChars[0] == this.toUnicode(this.locale.formatDecimal(1, '1.0-0', defaultLocale))) {
                decimalCode = {
                    minusSign: unicodeChars[7],
                    decimalSeparator: unicodeChars[5],
                    thousandSeparator: unicodeChars[1]
                };
            }
            else if (unicodeChars[0] == "\\u200E") {
                decimalCode = {
                    minusSign: unicodeChars[1],
                    decimalSeparator: unicodeChars[7],
                    thousandSeparator: unicodeChars[3]
                };
            }
            else {
                decimalCode = {
                    minusSign: unicodeChars[0],
                    decimalSeparator: unicodeChars[6],
                    thousandSeparator: unicodeChars[2]
                };
            }
        }
        return decimalCode;
    };
    /**
     * @param {?=} defaultLocale
     * @return {?}
     */
    LocaleValidation.prototype.getNumberCodes = /**
     * @param {?=} defaultLocale
     * @return {?}
     */
    function (defaultLocale) {
        /** @type {?} */
        var numberCodes = [];
        for (var num = 0; num <= 9; num++) {
            numberCodes.push(this.toUnicode(num.toString()));
        }
        if (IntlAPI.hasNumberFormat()) {
            for (var num = 0; num <= 9; num++) {
                numberCodes[num] = this.toUnicode(this.locale.formatDecimal(num, '1.0-0', defaultLocale));
            }
        }
        return numberCodes;
    };
    /**
     * @param {?} pattern
     * @return {?}
     */
    LocaleValidation.prototype.toChar = /**
     * @param {?} pattern
     * @return {?}
     */
    function (pattern) {
        return pattern.replace(/\\u[\dA-F]{4}/gi, function (match) {
            return String.fromCharCode(parseInt(match.replace(/\\u/g, ""), 16));
        });
    };
    /**
     * @param {?} c
     * @return {?}
     */
    LocaleValidation.prototype.toUnicode = /**
     * @param {?} c
     * @return {?}
     */
    function (c) {
        return "\\u" + this.toHex(c.charCodeAt(0));
    };
    /**
     * @param {?} value
     * @return {?}
     */
    LocaleValidation.prototype.toHex = /**
     * @param {?} value
     * @return {?}
     */
    function (value) {
        /** @type {?} */
        var hex = value.toString(16).toUpperCase();
        // With padding.
        hex = "0000".substr(0, 4 - hex.length) + hex;
        return hex;
    };
    LocaleValidation.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    LocaleValidation.ctorParameters = function () { return [
        { type: LocaleService }
    ]; };
    return LocaleValidation;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Intl.Collator APIs.
 * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Collator
 */
var Collator = /** @class */ (function () {
    function Collator(locale, translation) {
        this.locale = locale;
        this.translation = translation;
    }
    /**
     * Compares two keys by the value of translation according to the current language.
     * @param key1, key2 The keys of the values to compare
     * @param extension Unicode extension key, e.g. 'co-phonebk'
     * @param options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return A negative value if the value of translation of key1 comes before the value of translation of key2;
     *         a positive value if key1 comes after key2;
     *         0 if they are considered equal or Intl.Collator is not supported
     */
    /**
     * Compares two keys by the value of translation according to the current language.
     * @param {?} key1
     * @param {?} key2
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} A negative value if the value of translation of key1 comes before the value of translation of key2;
     *         a positive value if key1 comes after key2;
     *         0 if they are considered equal or Intl.Collator is not supported
     */
    Collator.prototype.compare = /**
     * Compares two keys by the value of translation according to the current language.
     * @param {?} key1
     * @param {?} key2
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} A negative value if the value of translation of key1 comes before the value of translation of key2;
     *         a positive value if key1 comes after key2;
     *         0 if they are considered equal or Intl.Collator is not supported
     */
    function (key1, key2, extension, options) {
        if (options === void 0) { options = { usage: 'sort', sensitivity: 'variant' }; }
        if (!IntlAPI.hasCollator())
            return 0;
        /** @type {?} */
        var value1 = this.translation.translate(key1);
        /** @type {?} */
        var value2 = this.translation.translate(key2);
        /** @type {?} */
        var locale = this.addExtension(this.locale.getCurrentLocale(), extension);
        return new Intl.Collator(locale, options).compare(value1, value2);
    };
    /**
     * Sorts an array of objects or an array of arrays according to the current language.
     * @param list The array to be sorted
     * @param keyName The column that contains the keys of the values to be ordered
     * @param order 'asc' or 'desc'. The default value is 'asc'
     * @param extension Unicode extension key, e.g. 'co-phonebk'
     * @param options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return The same sorted list or the same list if Intl.Collator is not supported
     */
    /**
     * Sorts an array of objects or an array of arrays according to the current language.
     * @param {?} list The array to be sorted
     * @param {?} keyName The column that contains the keys of the values to be ordered
     * @param {?=} order 'asc' or 'desc'. The default value is 'asc'
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} The same sorted list or the same list if Intl.Collator is not supported
     */
    Collator.prototype.sort = /**
     * Sorts an array of objects or an array of arrays according to the current language.
     * @param {?} list The array to be sorted
     * @param {?} keyName The column that contains the keys of the values to be ordered
     * @param {?=} order 'asc' or 'desc'. The default value is 'asc'
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} The same sorted list or the same list if Intl.Collator is not supported
     */
    function (list, keyName, order, extension, options) {
        var _this = this;
        if (order === void 0) { order = "asc"; }
        if (options === void 0) { options = { usage: 'sort', sensitivity: 'variant' }; }
        if (!list || !keyName || !IntlAPI.hasCollator())
            return list;
        list.sort(function (key1, key2) {
            return _this.compare(key1[keyName], key2[keyName], extension, options);
        });
        if (order == "desc") {
            list.reverse();
        }
        return list;
    };
    /**
     * Sorts asynchronously an array of objects or an array of arrays according to the current language.
     * @param list The array to be sorted
     * @param keyName The column that contains the keys of the values to be ordered
     * @param order 'asc' or 'desc'. The default value is 'asc'
     * @param extension Unicode extension key, e.g. 'co-phonebk'
     * @param options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return An observable of the sorted list or of the same list if Intl.Collator is not supported
     */
    /**
     * Sorts asynchronously an array of objects or an array of arrays according to the current language.
     * @param {?} list The array to be sorted
     * @param {?} keyName The column that contains the keys of the values to be ordered
     * @param {?=} order 'asc' or 'desc'. The default value is 'asc'
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} An observable of the sorted list or of the same list if Intl.Collator is not supported
     */
    Collator.prototype.sortAsync = /**
     * Sorts asynchronously an array of objects or an array of arrays according to the current language.
     * @param {?} list The array to be sorted
     * @param {?} keyName The column that contains the keys of the values to be ordered
     * @param {?=} order 'asc' or 'desc'. The default value is 'asc'
     * @param {?=} extension Unicode extension key, e.g. 'co-phonebk'
     * @param {?=} options Default is { usage: 'sort', sensitivity: 'variant' }
     * @return {?} An observable of the sorted list or of the same list if Intl.Collator is not supported
     */
    function (list, keyName, order, extension, options) {
        var _this = this;
        if (options === void 0) { options = { usage: 'sort', sensitivity: 'variant' }; }
        return Observable.create(function (observer) {
            observer.next(_this.sort(list, keyName, order, extension, options));
            observer.complete();
        });
    };
    /**
     * Matches a string into an array of objects or an array of arrays according to the current language.
     * @param s The string to search
     * @param list The array in which to search
     * @param keyNames An array that contains the columns to look for
     * @param options Default is { usage: 'search' }
     * @return A filtered list or the same list if Intl.Collator is not supported
     */
    /**
     * Matches a string into an array of objects or an array of arrays according to the current language.
     * @param {?} s The string to search
     * @param {?} list The array in which to search
     * @param {?} keyNames An array that contains the columns to look for
     * @param {?=} options Default is { usage: 'search' }
     * @return {?} A filtered list or the same list if Intl.Collator is not supported
     */
    Collator.prototype.search = /**
     * Matches a string into an array of objects or an array of arrays according to the current language.
     * @param {?} s The string to search
     * @param {?} list The array in which to search
     * @param {?} keyNames An array that contains the columns to look for
     * @param {?=} options Default is { usage: 'search' }
     * @return {?} A filtered list or the same list if Intl.Collator is not supported
     */
    function (s, list, keyNames, options) {
        var _this = this;
        if (options === void 0) { options = { usage: 'search' }; }
        if (!list || !keyNames || s == "" || s == null || !IntlAPI.hasCollator())
            return list;
        /** @type {?} */
        var locale = this.locale.getCurrentLocale();
        /** @type {?} */
        var collator = new Intl.Collator(locale, options);
        /** @type {?} */
        var matches = list.filter(function (key) {
            /** @type {?} */
            var found = false;
            for (var i = 0; i < keyNames.length; i++) {
                if (_this.match(key[keyNames[i]], s, collator)) {
                    found = true;
                    break;
                }
            }
            return found;
        });
        return matches;
    };
    /**
     * Matches asynchronously a string into an array of objects or an array of arrays according to the current language.
     * @param s The string to search
     * @param list The array in which to search
     * @param keyNames An array that contains the columns to look for
     * @param options Default is { usage: 'search' }
     * @return An observable of the filtered list or the same list if Intl.Collator is not supported
     */
    /**
     * Matches asynchronously a string into an array of objects or an array of arrays according to the current language.
     * @param {?} s The string to search
     * @param {?} list The array in which to search
     * @param {?} keyNames An array that contains the columns to look for
     * @param {?=} options Default is { usage: 'search' }
     * @return {?} An observable of the filtered list or the same list if Intl.Collator is not supported
     */
    Collator.prototype.searchAsync = /**
     * Matches asynchronously a string into an array of objects or an array of arrays according to the current language.
     * @param {?} s The string to search
     * @param {?} list The array in which to search
     * @param {?} keyNames An array that contains the columns to look for
     * @param {?=} options Default is { usage: 'search' }
     * @return {?} An observable of the filtered list or the same list if Intl.Collator is not supported
     */
    function (s, list, keyNames, options) {
        var _this = this;
        if (options === void 0) { options = { usage: 'search' }; }
        return Observable.create(function (observer) {
            observer.next(_this.search(s, list, keyNames, options));
            observer.complete();
        });
    };
    /**
     * @param {?} locale
     * @param {?=} extension
     * @return {?}
     */
    Collator.prototype.addExtension = /**
     * @param {?} locale
     * @param {?=} extension
     * @return {?}
     */
    function (locale, extension) {
        if (!!extension) {
            locale = locale + "-u-" + extension;
        }
        return locale;
    };
    /**
     * @param {?} key
     * @param {?} s
     * @param {?} collator
     * @return {?}
     */
    Collator.prototype.match = /**
     * @param {?} key
     * @param {?} s
     * @param {?} collator
     * @return {?}
     */
    function (key, s, collator) {
        /** @type {?} */
        var value = this.translation.translate(key);
        /** @type {?} */
        var valueLength = value.length;
        /** @type {?} */
        var sLength = s.length;
        if (sLength > valueLength) {
            return false;
        }
        if (sLength == valueLength) {
            return collator.compare(value, s) == 0;
        }
        /** @type {?} */
        var found = false;
        for (var i = 0; i < valueLength - (sLength - 1); i++) {
            /** @type {?} */
            var str = value.substr(i, sLength);
            if (collator.compare(str, s) == 0) {
                found = true;
                break;
            }
        }
        return found;
    };
    Collator.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    Collator.ctorParameters = function () { return [
        { type: LocaleService },
        { type: TranslationService }
    ]; };
    return Collator;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Manages the translation of page 'title' and meta tags.
 */
var SearchService = /** @class */ (function () {
    function SearchService(configuration, translation, title, meta) {
        this.configuration = configuration;
        this.translation = translation;
        this.title = title;
        this.meta = meta;
    }
    /**
     * Translates the 'title' of the page and the provided meta tags.
     * @param page The key path of the page
     */
    /**
     * Translates the 'title' of the page and the provided meta tags.
     * @param {?} page The key path of the page
     * @return {?}
     */
    SearchService.prototype.updateHead = /**
     * Translates the 'title' of the page and the provided meta tags.
     * @param {?} page The key path of the page
     * @return {?}
     */
    function (page) {
        var _this = this;
        this.translation.translationChanged().subscribe(function () {
            _this.setTitle(page);
            if (_this.configuration.search.metaTags) {
                for (var _i = 0, _a = _this.configuration.search.metaTags; _i < _a.length; _i++) {
                    var name_1 = _a[_i];
                    _this.updateTag(name_1, page);
                }
            }
        });
    };
    /**
     * @param {?} page
     * @return {?}
     */
    SearchService.prototype.setTitle = /**
     * @param {?} page
     * @return {?}
     */
    function (page) {
        this.title.setTitle(this.translation.translate(page + (this.configuration.translation.composedKeySeparator || '') + 'title'));
    };
    /**
     * @param {?} name
     * @param {?} page
     * @return {?}
     */
    SearchService.prototype.updateTag = /**
     * @param {?} name
     * @param {?} page
     * @return {?}
     */
    function (name, page) {
        this.meta.updateTag({
            name: name,
            content: this.translation.translate(page + (this.configuration.translation.composedKeySeparator || '') + name)
        });
    };
    SearchService.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    SearchService.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
        { type: TranslationService },
        { type: Title },
        { type: Meta }
    ]; };
    return SearchService;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var LocaleInterceptor = /** @class */ (function () {
    function LocaleInterceptor(configuration, locale) {
        this.configuration = configuration;
        this.locale = locale;
    }
    /**
     * @param {?} request
     * @param {?} next
     * @return {?}
     */
    LocaleInterceptor.prototype.intercept = /**
     * @param {?} request
     * @param {?} next
     * @return {?}
     */
    function (request, next) {
        if (this.configuration.localeInterceptor.format) {
            /** @type {?} */
            var locale = this.locale.composeLocale(this.configuration.localeInterceptor.format);
            if (!!locale) {
                request = request.clone({ setHeaders: { 'Accept-Language': locale } });
            }
        }
        return next.handle(request);
    };
    LocaleInterceptor.decorators = [
        { type: Injectable }
    ];
    /** @nocollapse */
    LocaleInterceptor.ctorParameters = function () { return [
        { type: undefined, decorators: [{ type: Inject, args: [L10N_CONFIG,] }] },
        { type: LocaleService }
    ]; };
    return LocaleInterceptor;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Property decorator for components to provide the parameter to translate pipe.
 * @return {?}
 */
function Language$1() {
    /**
     * @param {?} target
     * @param {?=} propertyKey
     * @return {?}
     */
    function DecoratorFactory(target, propertyKey) {
        /** @type {?} */
        var targetNgOnInit = target.ngOnInit;
        if (typeof targetNgOnInit === "undefined") {
            Logger.log(target.constructor ? target.constructor.name : 'Language decorator', 'missingOnInit');
        }
        /**
         * @this {?}
         * @return {?}
         */
        function ngOnInit() {
            var _this = this;
            /** @type {?} */
            var translation = InjectorRef.get(TranslationService);
            if (typeof propertyKey !== "undefined") {
                translation.latestTranslation().pipe(takeUntilDestroyed(this)).subscribe(function (language) {
                    _this[propertyKey] = language;
                    // OnPush Change Detection strategy.
                    /** @type {?} */
                    var cdr = Object.keys(_this)
                        .find(function (key) { return _this[key] && _this[key]['markForCheck'] !== undefined; });
                    if (cdr) {
                        _this[cdr].markForCheck();
                    }
                });
            }
            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }
        target.ngOnInit = ngOnInit;
        if (typeof propertyKey !== "undefined") {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value: ''
            });
        }
    }
    return DecoratorFactory;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Property decorator for components to provide the parameter
 * to l10nDate, l10nDecimal, l10nPercent & l10nCurrency pipes.
 * @return {?}
 */
function DefaultLocale$2() {
    /**
     * @param {?} target
     * @param {?=} propertyKey
     * @return {?}
     */
    function DecoratorFactory(target, propertyKey) {
        /** @type {?} */
        var targetNgOnInit = target.ngOnInit;
        if (typeof targetNgOnInit === "undefined") {
            Logger.log(target.constructor ? target.constructor.name : 'DefaultLocale decorator', 'missingOnInit');
        }
        /**
         * @this {?}
         * @return {?}
         */
        function ngOnInit() {
            var _this = this;
            /** @type {?} */
            var locale = InjectorRef.get(LocaleService);
            if (typeof propertyKey !== "undefined") {
                this[propertyKey] = locale.getDefaultLocale();
                locale.defaultLocaleChanged.pipe(takeUntilDestroyed(this)).subscribe(function (defaultLocale) {
                    _this[propertyKey] = defaultLocale;
                    /** @type {?} */
                    var cdr = Object.keys(_this)
                        .find(function (key) { return _this[key] && _this[key]['markForCheck'] !== undefined; });
                    if (cdr) {
                        _this[cdr].markForCheck();
                    }
                });
            }
            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }
        target.ngOnInit = ngOnInit;
        if (typeof propertyKey !== "undefined") {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value: ''
            });
        }
    }
    return DecoratorFactory;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Property decorator for components to provide the parameter to the l10nCurrency pipe.
 * @return {?}
 */
function Currency() {
    /**
     * @param {?} target
     * @param {?=} propertyKey
     * @return {?}
     */
    function DecoratorFactory(target, propertyKey) {
        /** @type {?} */
        var targetNgOnInit = target.ngOnInit;
        if (typeof targetNgOnInit === "undefined") {
            Logger.log(target.constructor ? target.constructor.name : 'Currency decorator', 'missingOnInit');
        }
        /**
         * @this {?}
         * @return {?}
         */
        function ngOnInit() {
            var _this = this;
            /** @type {?} */
            var locale = InjectorRef.get(LocaleService);
            if (typeof propertyKey !== "undefined") {
                this[propertyKey] = locale.getCurrentCurrency();
                locale.currencyCodeChanged.pipe(takeUntilDestroyed(this)).subscribe(function (currency) {
                    _this[propertyKey] = currency;
                    /** @type {?} */
                    var cdr = Object.keys(_this)
                        .find(function (key) { return _this[key] && _this[key]['markForCheck'] !== undefined; });
                    if (cdr) {
                        _this[cdr].markForCheck();
                    }
                });
            }
            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }
        target.ngOnInit = ngOnInit;
        if (typeof propertyKey !== "undefined") {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value: ''
            });
        }
    }
    return DecoratorFactory;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Property decorator for components to provide the parameter to the l10nDate pipe.
 * @return {?}
 */
function Timezone() {
    /**
     * @param {?} target
     * @param {?=} propertyKey
     * @return {?}
     */
    function DecoratorFactory(target, propertyKey) {
        /** @type {?} */
        var targetNgOnInit = target.ngOnInit;
        if (typeof targetNgOnInit === "undefined") {
            Logger.log(target.constructor ? target.constructor.name : 'Timezone decorator', 'missingOnInit');
        }
        /**
         * @this {?}
         * @return {?}
         */
        function ngOnInit() {
            var _this = this;
            /** @type {?} */
            var locale = InjectorRef.get(LocaleService);
            if (typeof propertyKey !== "undefined") {
                this[propertyKey] = locale.getCurrentTimezone();
                locale.timezoneChanged.pipe(takeUntilDestroyed(this)).subscribe(function (zoneName) {
                    _this[propertyKey] = zoneName;
                    /** @type {?} */
                    var cdr = Object.keys(_this)
                        .find(function (key) { return _this[key] && _this[key]['markForCheck'] !== undefined; });
                    if (cdr) {
                        _this[cdr].markForCheck();
                    }
                });
            }
            if (targetNgOnInit) {
                targetNgOnInit.apply(this);
            }
        }
        target.ngOnInit = ngOnInit;
        if (typeof propertyKey !== "undefined") {
            Object.defineProperty(target, propertyKey, {
                writable: true,
                value: ''
            });
        }
    }
    return DecoratorFactory;
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var TranslatePipe = /** @class */ (function () {
    function TranslatePipe(translation) {
        this.translation = translation;
    }
    /**
     * @param {?} key
     * @param {?} lang
     * @param {?=} args
     * @return {?}
     */
    TranslatePipe.prototype.transform = /**
     * @param {?} key
     * @param {?} lang
     * @param {?=} args
     * @return {?}
     */
    function (key, lang, args) {
        if (key == null || key === "")
            return null;
        if (typeof lang === "undefined")
            Logger.log('TranslatePipe', 'missingLang');
        return this.translation.translate(key, args, lang);
    };
    TranslatePipe.decorators = [
        { type: Pipe, args: [{
                    name: 'translate',
                    pure: true
                },] }
    ];
    /** @nocollapse */
    TranslatePipe.ctorParameters = function () { return [
        { type: TranslationService }
    ]; };
    return TranslatePipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var L10nDatePipe = /** @class */ (function () {
    function L10nDatePipe(locale) {
        this.locale = locale;
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} format
     * @param {?=} timezone
     * @return {?}
     */
    L10nDatePipe.prototype.transform = /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} format
     * @param {?=} timezone
     * @return {?}
     */
    function (value, defaultLocale, format, timezone) {
        if (value == null || value === "" || value !== value)
            return null;
        if (typeof defaultLocale === "undefined")
            Logger.log('L10nDatePipe', 'missingDefaultLocale');
        return this.locale.formatDate(value, format, defaultLocale, timezone);
    };
    L10nDatePipe.decorators = [
        { type: Pipe, args: [{
                    name: 'l10nDate',
                    pure: true
                },] }
    ];
    /** @nocollapse */
    L10nDatePipe.ctorParameters = function () { return [
        { type: LocaleService }
    ]; };
    return L10nDatePipe;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var L10nDecimalPipe = /** @class */ (function () {
    function L10nDecimalPipe(locale) {
        this.locale = locale;
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} digits
     * @return {?}
     */
    L10nDecimalPipe.prototype.transform = /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} digits
     * @return {?}
     */
    function (value, defaultLocale, digits) {
        if (isEmpty(value))
            return null;
        if (typeof defaultLocale === "undefined")
            Logger.log('L10nDecimalPipe', 'missingDefaultLocale');
        return this.locale.formatDecimal(value, digits, defaultLocale);
    };
    L10nDecimalPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'l10nDecimal',
                    pure: true
                },] }
    ];
    /** @nocollapse */
    L10nDecimalPipe.ctorParameters = function () { return [
        { type: LocaleService }
    ]; };
    return L10nDecimalPipe;
}());
var L10nPercentPipe = /** @class */ (function () {
    function L10nPercentPipe(locale) {
        this.locale = locale;
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} digits
     * @return {?}
     */
    L10nPercentPipe.prototype.transform = /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?=} digits
     * @return {?}
     */
    function (value, defaultLocale, digits) {
        if (isEmpty(value))
            return null;
        if (typeof defaultLocale === "undefined")
            Logger.log('L10nPercentPipe', 'missingDefaultLocale');
        return this.locale.formatPercent(value, digits, defaultLocale);
    };
    L10nPercentPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'l10nPercent',
                    pure: true
                },] }
    ];
    /** @nocollapse */
    L10nPercentPipe.ctorParameters = function () { return [
        { type: LocaleService }
    ]; };
    return L10nPercentPipe;
}());
var L10nCurrencyPipe = /** @class */ (function () {
    function L10nCurrencyPipe(locale) {
        this.locale = locale;
    }
    /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} currency
     * @param {?=} currencyDisplay
     * @param {?=} digits
     * @return {?}
     */
    L10nCurrencyPipe.prototype.transform = /**
     * @param {?} value
     * @param {?} defaultLocale
     * @param {?} currency
     * @param {?=} currencyDisplay
     * @param {?=} digits
     * @return {?}
     */
    function (value, defaultLocale, currency, currencyDisplay, digits) {
        if (isEmpty(value))
            return null;
        if (typeof defaultLocale === "undefined")
            Logger.log('L10nCurrencyPipe', 'missingDefaultLocale');
        if (typeof currency === "undefined")
            Logger.log('L10nCurrencyPipe', 'missingCurrency');
        return this.locale.formatCurrency(value, digits, currencyDisplay, defaultLocale, currency);
    };
    L10nCurrencyPipe.decorators = [
        { type: Pipe, args: [{
                    name: 'l10nCurrency',
                    pure: true
                },] }
    ];
    /** @nocollapse */
    L10nCurrencyPipe.ctorParameters = function () { return [
        { type: LocaleService }
    ]; };
    return L10nCurrencyPipe;
}());
/**
 * @param {?} value
 * @return {?}
 */
function isEmpty(value) {
    return value == null || value === "" || value !== value; // Checks for NaN.
}

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Breadth First Search (BFS) algorithm for traversing & searching tree data structure of DOM
 * explores the neighbor nodes first, before moving to the next level neighbors.
 * Time complexity: between O(1) and O(|V|^2).
 */
var BFS = /** @class */ (function () {
    function BFS() {
    }
    /**
     * Target node is a non empty text node.
     */
    /**
     * Target node is a non empty text node.
     * @param {?} rootNode
     * @return {?}
     */
    BFS.getTargetNode = /**
     * Target node is a non empty text node.
     * @param {?} rootNode
     * @return {?}
     */
    function (rootNode) {
        return BFS.walk(rootNode);
    };
    /**
     * @param {?} rootNode
     * @return {?}
     */
    BFS.walk = /**
     * @param {?} rootNode
     * @return {?}
     */
    function (rootNode) {
        /** @type {?} */
        var queue = [];
        /** @type {?} */
        var iNode;
        /** @type {?} */
        var depth = 0;
        /** @type {?} */
        var nodeToDepthIncrease = 1;
        queue.push(rootNode);
        while (queue.length > 0 && depth <= BFS.MAX_DEPTH) {
            iNode = queue.shift();
            if (BFS.isTargetNode(iNode)) {
                return iNode;
            }
            if (depth < BFS.MAX_DEPTH && iNode.childNodes != null) {
                for (var _i = 0, _a = iNode.childNodes; _i < _a.length; _i++) {
                    var child = _a[_i];
                    if (BFS.isValidNode(child)) {
                        queue.push(child);
                    }
                }
            }
            if (--nodeToDepthIncrease == 0) {
                depth++;
                nodeToDepthIncrease = queue.length;
            }
        }
        return rootNode;
    };
    /**
     * @param {?} node
     * @return {?}
     */
    BFS.isTargetNode = /**
     * @param {?} node
     * @return {?}
     */
    function (node) {
        return typeof node !== "undefined" &&
            node.nodeType == 3 &&
            node.nodeValue != null &&
            node.nodeValue.trim() != "";
    };
    /**
     * A valid node is not marked for translation.
     */
    /**
     * A valid node is not marked for translation.
     * @param {?} node
     * @return {?}
     */
    BFS.isValidNode = /**
     * A valid node is not marked for translation.
     * @param {?} node
     * @return {?}
     */
    function (node) {
        if (typeof node !== "undefined" && node.nodeType == 1 && node.attributes) {
            for (var _i = 0, _a = node.attributes; _i < _a.length; _i++) {
                var attr = _a[_i];
                if (attr && BFS.SELECTOR.test(attr.name))
                    return false;
            }
        }
        return true;
    };
    BFS.SELECTOR = new RegExp("^l10n|translate|locale");
    BFS.MAX_DEPTH = 10;
    return BFS;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @abstract
 */
var BaseDirective = /** @class */ (function () {
    function BaseDirective(el, renderer) {
        this.el = el;
        this.renderer = renderer;
        this.attributes = [];
        this.destroy = new Subject();
        this.TEXT_MUTATION_CONFIG = { subtree: true, characterData: true };
        this.SELECTOR = /^l10n-/;
    }
    /**
     * @return {?}
     */
    BaseDirective.prototype.ngAfterViewInit = /**
     * @return {?}
     */
    function () {
        if (this.el && this.el.nativeElement) {
            this.element = this.el.nativeElement;
            this.renderNode = BFS.getTargetNode(this.element);
            this.getKey();
            this.getAttributes();
            this.addTextListener();
            this.setup();
        }
    };
    /**
     * @param {?} changes
     * @return {?}
     */
    BaseDirective.prototype.ngOnChanges = /**
     * @param {?} changes
     * @return {?}
     */
    function (changes) {
        if (!!this.key) {
            if (this.nodeValue == null || this.nodeValue == "") {
                if (!!this.valueAttribute) {
                    this.key = this.valueAttribute;
                }
                else if (!!this.innerHTMLProperty) {
                    this.key = this.innerHTMLProperty;
                }
            }
            this.replaceText();
        }
        if (this.attributes.length > 0) {
            this.replaceAttributes();
        }
    };
    /**
     * @return {?}
     */
    BaseDirective.prototype.ngOnDestroy = /**
     * @return {?}
     */
    function () {
        this.destroy.next(true);
        this.removeTextListener();
    };
    /**
     * @return {?}
     */
    BaseDirective.prototype.getAttributesData = /**
     * @return {?}
     */
    function () {
        /** @type {?} */
        var keys = this.getAttributesKeys();
        /** @type {?} */
        var data = {};
        for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
            var key = keys_1[_i];
            data[key] = this.getValue(key);
        }
        return data;
    };
    /**
     * @return {?}
     */
    BaseDirective.prototype.getAttributesKeys = /**
     * @return {?}
     */
    function () {
        return this.attributes.map(function (attr) { return attr.key; });
    };
    /**
     * @param {?} value
     * @return {?}
     */
    BaseDirective.prototype.setText = /**
     * @param {?} value
     * @return {?}
     */
    function (value) {
        if (!!value) {
            if (!!this.nodeValue && !!this.key) {
                this.removeTextListener();
                this.renderer.setValue(this.renderNode, this.nodeValue.replace(this.key, value));
                this.addTextListener();
            }
            else if (!!this.valueAttribute) {
                this.renderer.setAttribute(this.element, "value", value);
            }
            else if (!!this.innerHTMLProperty) {
                this.renderer.setProperty(this.element, "innerHTML", value);
            }
        }
    };
    /**
     * @param {?} data
     * @return {?}
     */
    BaseDirective.prototype.setAttributes = /**
     * @param {?} data
     * @return {?}
     */
    function (data) {
        for (var _i = 0, _a = this.attributes; _i < _a.length; _i++) {
            var attr = _a[_i];
            this.renderer.setAttribute(this.element, attr.name, data[attr.key]);
        }
    };
    /**
     * @return {?}
     */
    BaseDirective.prototype.addTextListener = /**
     * @return {?}
     */
    function () {
        var _this = this;
        if (typeof MutationObserver !== "undefined") {
            this.textObserver = new MutationObserver(function (mutations) {
                _this.renderNode = BFS.getTargetNode(_this.element);
                _this.getKey();
                _this.replaceText();
            });
            this.textObserver.observe(this.renderNode, this.TEXT_MUTATION_CONFIG);
        }
    };
    /**
     * @return {?}
     */
    BaseDirective.prototype.removeTextListener = /**
     * @return {?}
     */
    function () {
        if (typeof this.textObserver !== "undefined") {
            this.textObserver.disconnect();
        }
    };
    /**
     * @return {?}
     */
    BaseDirective.prototype.getText = /**
     * @return {?}
     */
    function () {
        this.nodeValue = this.renderNode != null ? (/** @type {?} */ (this.renderNode.nodeValue)) : "";
        return !!this.nodeValue ? this.nodeValue.trim() : "";
    };
    /**
     * @return {?}
     */
    BaseDirective.prototype.getKey = /**
     * @return {?}
     */
    function () {
        if (this.element.childNodes.length > 0) {
            this.key = this.getText();
        }
        else if (!!this.valueAttribute) {
            this.key = this.valueAttribute;
        }
        else if (!!this.innerHTMLProperty) {
            this.key = this.innerHTMLProperty;
        }
    };
    /**
     * @return {?}
     */
    BaseDirective.prototype.getAttributes = /**
     * @return {?}
     */
    function () {
        if (this.element.attributes) {
            for (var _i = 0, _a = this.element.attributes; _i < _a.length; _i++) {
                var attr = _a[_i];
                if (attr && this.SELECTOR.test(attr.name)) {
                    /** @type {?} */
                    var name_1 = attr.name.substr(5);
                    for (var _b = 0, _c = this.element.attributes; _b < _c.length; _b++) {
                        var targetAttr = _c[_b];
                        if (new RegExp("^" + name_1 + "$").test(targetAttr.name)) {
                            this.attributes.push({ name: name_1, key: targetAttr.value });
                        }
                    }
                }
            }
        }
    };
    BaseDirective.propDecorators = {
        valueAttribute: [{ type: Input, args: ['value',] }],
        innerHTMLProperty: [{ type: Input, args: ['innerHTML',] }]
    };
    return BaseDirective;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var TranslateDirective = /** @class */ (function (_super) {
    __extends(TranslateDirective, _super);
    function TranslateDirective(translation, el, renderer) {
        var _this = _super.call(this, el, renderer) || this;
        _this.translation = translation;
        _this.el = el;
        _this.renderer = renderer;
        return _this;
    }
    Object.defineProperty(TranslateDirective.prototype, "l10nTranslate", {
        set: /**
         * @param {?} params
         * @return {?}
         */
        function (params) {
            this.params = params;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(TranslateDirective.prototype, "translate", {
        set: /**
         * @param {?} params
         * @return {?}
         */
        function (params) {
            this.params = params;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    TranslateDirective.prototype.setup = /**
     * @return {?}
     */
    function () {
        var _this = this;
        this.translation.translationChanged().pipe(takeUntil(this.destroy)).subscribe(function () { _this.replace(); });
    };
    /**
     * @return {?}
     */
    TranslateDirective.prototype.replace = /**
     * @return {?}
     */
    function () {
        this.replaceText();
        this.replaceAttributes();
    };
    /**
     * @return {?}
     */
    TranslateDirective.prototype.replaceText = /**
     * @return {?}
     */
    function () {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    };
    /**
     * @return {?}
     */
    TranslateDirective.prototype.replaceAttributes = /**
     * @return {?}
     */
    function () {
        if (this.attributes.length > 0) {
            /** @type {?} */
            var keys = this.getAttributesKeys();
            /** @type {?} */
            var data = this.translation.translate(keys, this.params);
            this.setAttributes(data);
        }
    };
    /**
     * @param {?} key
     * @return {?}
     */
    TranslateDirective.prototype.getValue = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return this.translation.translate(key, this.params);
    };
    TranslateDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[l10nTranslate],[translate]'
                },] }
    ];
    /** @nocollapse */
    TranslateDirective.ctorParameters = function () { return [
        { type: TranslationService },
        { type: ElementRef },
        { type: Renderer2 }
    ]; };
    TranslateDirective.propDecorators = {
        l10nTranslate: [{ type: Input }],
        translate: [{ type: Input }],
        params: [{ type: Input }]
    };
    return TranslateDirective;
}(BaseDirective));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var L10nDateDirective = /** @class */ (function (_super) {
    __extends(L10nDateDirective, _super);
    function L10nDateDirective(locale, el, renderer) {
        var _this = _super.call(this, el, renderer) || this;
        _this.locale = locale;
        _this.el = el;
        _this.renderer = renderer;
        return _this;
    }
    Object.defineProperty(L10nDateDirective.prototype, "l10nDate", {
        set: /**
         * @param {?} format
         * @return {?}
         */
        function (format) {
            this.format = format;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    L10nDateDirective.prototype.setup = /**
     * @return {?}
     */
    function () {
        var _this = this;
        this.replace();
        this.locale.defaultLocaleChanged.pipe(takeUntil(this.destroy)).subscribe(function () { _this.replace(); });
        this.locale.timezoneChanged.pipe(takeUntil(this.destroy)).subscribe(function () { _this.replace(); });
    };
    /**
     * @return {?}
     */
    L10nDateDirective.prototype.replace = /**
     * @return {?}
     */
    function () {
        this.replaceText();
        this.replaceAttributes();
    };
    /**
     * @return {?}
     */
    L10nDateDirective.prototype.replaceText = /**
     * @return {?}
     */
    function () {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    };
    /**
     * @return {?}
     */
    L10nDateDirective.prototype.replaceAttributes = /**
     * @return {?}
     */
    function () {
        if (this.attributes.length > 0) {
            this.setAttributes(this.getAttributesData());
        }
    };
    /**
     * @param {?} key
     * @return {?}
     */
    L10nDateDirective.prototype.getValue = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return this.locale.formatDate(key, this.format);
    };
    L10nDateDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[l10nDate]'
                },] }
    ];
    /** @nocollapse */
    L10nDateDirective.ctorParameters = function () { return [
        { type: LocaleService },
        { type: ElementRef },
        { type: Renderer2 }
    ]; };
    L10nDateDirective.propDecorators = {
        l10nDate: [{ type: Input }],
        format: [{ type: Input }]
    };
    return L10nDateDirective;
}(BaseDirective));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var L10nDecimalDirective = /** @class */ (function (_super) {
    __extends(L10nDecimalDirective, _super);
    function L10nDecimalDirective(locale, el, renderer) {
        var _this = _super.call(this, el, renderer) || this;
        _this.locale = locale;
        _this.el = el;
        _this.renderer = renderer;
        return _this;
    }
    Object.defineProperty(L10nDecimalDirective.prototype, "l10nDecimal", {
        set: /**
         * @param {?} digits
         * @return {?}
         */
        function (digits) {
            this.digits = digits;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    L10nDecimalDirective.prototype.setup = /**
     * @return {?}
     */
    function () {
        var _this = this;
        this.replace();
        this.locale.defaultLocaleChanged.pipe(takeUntil(this.destroy)).subscribe(function () { _this.replace(); });
    };
    /**
     * @return {?}
     */
    L10nDecimalDirective.prototype.replace = /**
     * @return {?}
     */
    function () {
        this.replaceText();
        this.replaceAttributes();
    };
    /**
     * @return {?}
     */
    L10nDecimalDirective.prototype.replaceText = /**
     * @return {?}
     */
    function () {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    };
    /**
     * @return {?}
     */
    L10nDecimalDirective.prototype.replaceAttributes = /**
     * @return {?}
     */
    function () {
        if (this.attributes.length > 0) {
            this.setAttributes(this.getAttributesData());
        }
    };
    /**
     * @param {?} key
     * @return {?}
     */
    L10nDecimalDirective.prototype.getValue = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return this.locale.formatDecimal(key, this.digits);
    };
    L10nDecimalDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[l10nDecimal]'
                },] }
    ];
    /** @nocollapse */
    L10nDecimalDirective.ctorParameters = function () { return [
        { type: LocaleService },
        { type: ElementRef },
        { type: Renderer2 }
    ]; };
    L10nDecimalDirective.propDecorators = {
        l10nDecimal: [{ type: Input }],
        digits: [{ type: Input }]
    };
    return L10nDecimalDirective;
}(BaseDirective));
var L10nPercentDirective = /** @class */ (function (_super) {
    __extends(L10nPercentDirective, _super);
    function L10nPercentDirective(locale, el, renderer) {
        var _this = _super.call(this, el, renderer) || this;
        _this.locale = locale;
        _this.el = el;
        _this.renderer = renderer;
        return _this;
    }
    Object.defineProperty(L10nPercentDirective.prototype, "l10nPercent", {
        set: /**
         * @param {?} digits
         * @return {?}
         */
        function (digits) {
            this.digits = digits;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    L10nPercentDirective.prototype.setup = /**
     * @return {?}
     */
    function () {
        var _this = this;
        this.replace();
        this.locale.defaultLocaleChanged.pipe(takeUntil(this.destroy)).subscribe(function () { _this.replace(); });
    };
    /**
     * @return {?}
     */
    L10nPercentDirective.prototype.replace = /**
     * @return {?}
     */
    function () {
        this.replaceText();
        this.replaceAttributes();
    };
    /**
     * @return {?}
     */
    L10nPercentDirective.prototype.replaceText = /**
     * @return {?}
     */
    function () {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    };
    /**
     * @return {?}
     */
    L10nPercentDirective.prototype.replaceAttributes = /**
     * @return {?}
     */
    function () {
        if (this.attributes.length > 0) {
            this.setAttributes(this.getAttributesData());
        }
    };
    /**
     * @param {?} key
     * @return {?}
     */
    L10nPercentDirective.prototype.getValue = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return this.locale.formatPercent(key, this.digits);
    };
    L10nPercentDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[l10nPercent]'
                },] }
    ];
    /** @nocollapse */
    L10nPercentDirective.ctorParameters = function () { return [
        { type: LocaleService },
        { type: ElementRef },
        { type: Renderer2 }
    ]; };
    L10nPercentDirective.propDecorators = {
        l10nPercent: [{ type: Input }],
        digits: [{ type: Input }]
    };
    return L10nPercentDirective;
}(BaseDirective));
var L10nCurrencyDirective = /** @class */ (function (_super) {
    __extends(L10nCurrencyDirective, _super);
    function L10nCurrencyDirective(locale, el, renderer) {
        var _this = _super.call(this, el, renderer) || this;
        _this.locale = locale;
        _this.el = el;
        _this.renderer = renderer;
        return _this;
    }
    Object.defineProperty(L10nCurrencyDirective.prototype, "l10nCurrency", {
        set: /**
         * @param {?} digits
         * @return {?}
         */
        function (digits) {
            this.digits = digits;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    L10nCurrencyDirective.prototype.setup = /**
     * @return {?}
     */
    function () {
        var _this = this;
        this.replace();
        this.locale.defaultLocaleChanged.pipe(takeUntil(this.destroy)).subscribe(function () { _this.replace(); });
        this.locale.currencyCodeChanged.pipe(takeUntil(this.destroy)).subscribe(function () { _this.replace(); });
    };
    /**
     * @return {?}
     */
    L10nCurrencyDirective.prototype.replace = /**
     * @return {?}
     */
    function () {
        this.replaceText();
        this.replaceAttributes();
    };
    /**
     * @return {?}
     */
    L10nCurrencyDirective.prototype.replaceText = /**
     * @return {?}
     */
    function () {
        if (!!this.key) {
            this.setText(this.getValue(this.key));
        }
    };
    /**
     * @return {?}
     */
    L10nCurrencyDirective.prototype.replaceAttributes = /**
     * @return {?}
     */
    function () {
        if (this.attributes.length > 0) {
            this.setAttributes(this.getAttributesData());
        }
    };
    /**
     * @param {?} key
     * @return {?}
     */
    L10nCurrencyDirective.prototype.getValue = /**
     * @param {?} key
     * @return {?}
     */
    function (key) {
        return this.locale.formatCurrency(key, this.digits, this.currencyDisplay);
    };
    L10nCurrencyDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[l10nCurrency]'
                },] }
    ];
    /** @nocollapse */
    L10nCurrencyDirective.ctorParameters = function () { return [
        { type: LocaleService },
        { type: ElementRef },
        { type: Renderer2 }
    ]; };
    L10nCurrencyDirective.propDecorators = {
        l10nCurrency: [{ type: Input }],
        currencyDisplay: [{ type: Input }],
        digits: [{ type: Input }]
    };
    return L10nCurrencyDirective;
}(BaseDirective));

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Function that takes a control and returns either null when it’s valid, or an error object if it’s not.
 * @param {?} digits An alias of the format
 * @param {?=} MIN_VALUE The minimum value for the number
 * @param {?=} MAX_VALUE The maximum value for the number
 * @return {?} An error object: 'format', 'minValue' or 'maxValue'; null in case the value is valid
 */
function l10nValidateNumber(digits, MIN_VALUE, MAX_VALUE) {
    if (MIN_VALUE === void 0) { MIN_VALUE = Number.MIN_VALUE; }
    if (MAX_VALUE === void 0) { MAX_VALUE = Number.MAX_VALUE; }
    /** @type {?} */
    var localeValidation = InjectorRef.get(LocaleValidation);
    return function (c) {
        if (c.value == "" || c.value == null)
            return null;
        /** @type {?} */
        var parsedValue = localeValidation.parseNumber(c.value, digits);
        if (parsedValue != null && !isNaN(parsedValue)) {
            if (parsedValue < MIN_VALUE) {
                return { minValue: true };
            }
            else if (parsedValue > MAX_VALUE) {
                return { maxValue: true };
            }
            return null; // The number is valid.
        }
        else {
            return { format: true };
        }
    };
}
var L10nNumberValidatorDirective = /** @class */ (function () {
    function L10nNumberValidatorDirective() {
        this.MIN_VALUE = Number.MIN_VALUE;
        this.MAX_VALUE = Number.MAX_VALUE;
    }
    Object.defineProperty(L10nNumberValidatorDirective.prototype, "l10nValidateNumber", {
        set: /**
         * @param {?} digits
         * @return {?}
         */
        function (digits) {
            this.digits = digits;
        },
        enumerable: true,
        configurable: true
    });
    /**
     * @return {?}
     */
    L10nNumberValidatorDirective.prototype.ngOnInit = /**
     * @return {?}
     */
    function () {
        this.validator = l10nValidateNumber(this.digits, this.minValue || this.MIN_VALUE, this.maxValue || this.MAX_VALUE);
    };
    /**
     * @param {?} c
     * @return {?}
     */
    L10nNumberValidatorDirective.prototype.validate = /**
     * @param {?} c
     * @return {?}
     */
    function (c) {
        return this.validator(c);
    };
    L10nNumberValidatorDirective.decorators = [
        { type: Directive, args: [{
                    selector: '[l10nValidateNumber][ngModel],[l10nValidateNumber][formControl],[l10nValidateNumber][formControlName]',
                    providers: [
                        { provide: NG_VALIDATORS, useExisting: forwardRef(function () { return L10nNumberValidatorDirective; }), multi: true }
                    ]
                },] }
    ];
    L10nNumberValidatorDirective.propDecorators = {
        l10nValidateNumber: [{ type: Input }],
        digits: [{ type: Input }],
        minValue: [{ type: Input }],
        maxValue: [{ type: Input }]
    };
    return L10nNumberValidatorDirective;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
var L10nJsonLdComponent = /** @class */ (function () {
    function L10nJsonLdComponent(translation, sanitizer) {
        this.translation = translation;
        this.sanitizer = sanitizer;
        this.destroy = new Subject();
    }
    /**
     * @return {?}
     */
    L10nJsonLdComponent.prototype.ngOnInit = /**
     * @return {?}
     */
    function () {
        var _this = this;
        this.translation.translationChanged().pipe(takeUntil(this.destroy)).subscribe(function () {
            /** @type {?} */
            var schema = _this.translation.translate(_this.path);
            _this.jsonLD = schema ? _this.sanitize(schema) : "";
        });
    };
    /**
     * @return {?}
     */
    L10nJsonLdComponent.prototype.ngOnDestroy = /**
     * @return {?}
     */
    function () {
        this.destroy.next(true);
    };
    /**
     * @param {?} schema
     * @return {?}
     */
    L10nJsonLdComponent.prototype.sanitize = /**
     * @param {?} schema
     * @return {?}
     */
    function (schema) {
        /** @type {?} */
        var json = JSON.stringify(schema, null, 2);
        /** @type {?} */
        var html = "<script type=\"application/ld+json\">" + json + "</script>";
        return this.sanitizer.bypassSecurityTrustHtml(html);
    };
    L10nJsonLdComponent.decorators = [
        { type: Component, args: [{
                    selector: 'l10n-json-ld',
                    template: ''
                }] }
    ];
    /** @nocollapse */
    L10nJsonLdComponent.ctorParameters = function () { return [
        { type: TranslationService },
        { type: DomSanitizer }
    ]; };
    L10nJsonLdComponent.propDecorators = {
        path: [{ type: Input }],
        jsonLD: [{ type: HostBinding, args: ['innerHTML',] }]
    };
    return L10nJsonLdComponent;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * @param {?} l10nConfig
 * @param {?} token
 * @return {?}
 */
function provideRoot(l10nConfig, token) {
    return [
        InjectorRef,
        Logger,
        Caching,
        { provide: L10N_CONFIG, useValue: l10nConfigFactory(l10nConfig) },
        LocaleService,
        { provide: LocaleStorage, useClass: token.localeStorage || L10nStorage },
        TranslationService,
        { provide: TranslationProvider, useClass: token.translationProvider || L10nTranslationProvider },
        { provide: TranslationHandler, useClass: token.translationHandler || L10nTranslationHandler },
        { provide: L10nLoader, useClass: LocaleLoader }
    ];
}
/**
 * @param {?} l10nConfig
 * @param {?} token
 * @return {?}
 */
function provideChild(l10nConfig, token) {
    return [
        { provide: L10N_CONFIG, useValue: l10nConfigFactory(l10nConfig) },
        TranslationService,
        { provide: TranslationProvider, useClass: token.translationProvider || L10nTranslationProvider },
        { provide: TranslationHandler, useClass: token.translationHandler || L10nTranslationHandler },
        { provide: L10nLoader, useClass: TranslationLoader }
    ];
}
/**
 * Provides dependencies, pipes & directives for translating messages.
 */
var TranslationModule = /** @class */ (function () {
    function TranslationModule(injector, logger) {
        this.injector = injector;
        this.logger = logger;
    }
    /**
     * Use in AppModule: new instances of LocaleService & TranslationService.
     */
    /**
     * Use in AppModule: new instances of LocaleService & TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    TranslationModule.forRoot = /**
     * Use in AppModule: new instances of LocaleService & TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    function (l10nConfig, token) {
        if (token === void 0) { token = {}; }
        return {
            ngModule: TranslationModule,
            providers: provideRoot(l10nConfig, token)
        };
    };
    /**
     * Use in feature modules with lazy loading: new instance of TranslationService.
     */
    /**
     * Use in feature modules with lazy loading: new instance of TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    TranslationModule.forChild = /**
     * Use in feature modules with lazy loading: new instance of TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    function (l10nConfig, token) {
        if (token === void 0) { token = {}; }
        return {
            ngModule: TranslationModule,
            providers: provideChild(l10nConfig, token)
        };
    };
    TranslationModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        TranslatePipe,
                        TranslateDirective
                    ],
                    exports: [
                        TranslatePipe,
                        TranslateDirective
                    ]
                },] }
    ];
    /** @nocollapse */
    TranslationModule.ctorParameters = function () { return [
        { type: InjectorRef },
        { type: Logger }
    ]; };
    return TranslationModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides dependencies, pipes & directives for translating messages, dates & numbers.
 */
var LocalizationModule = /** @class */ (function () {
    function LocalizationModule(injector, logger) {
        this.injector = injector;
        this.logger = logger;
    }
    /**
     * Use in AppModule: new instances of LocaleService & TranslationService.
     */
    /**
     * Use in AppModule: new instances of LocaleService & TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    LocalizationModule.forRoot = /**
     * Use in AppModule: new instances of LocaleService & TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    function (l10nConfig, token) {
        if (token === void 0) { token = {}; }
        return {
            ngModule: LocalizationModule,
            providers: provideRoot(l10nConfig, token)
        };
    };
    /**
     * Use in feature modules with lazy loading: new instance of TranslationService.
     */
    /**
     * Use in feature modules with lazy loading: new instance of TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    LocalizationModule.forChild = /**
     * Use in feature modules with lazy loading: new instance of TranslationService.
     * @param {?} l10nConfig
     * @param {?=} token
     * @return {?}
     */
    function (l10nConfig, token) {
        if (token === void 0) { token = {}; }
        return {
            ngModule: LocalizationModule,
            providers: provideChild(l10nConfig, token)
        };
    };
    LocalizationModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        L10nDatePipe,
                        L10nDecimalPipe,
                        L10nPercentPipe,
                        L10nCurrencyPipe,
                        L10nDateDirective,
                        L10nDecimalDirective,
                        L10nPercentDirective,
                        L10nCurrencyDirective
                    ],
                    imports: [
                        TranslationModule
                    ],
                    exports: [
                        TranslationModule,
                        L10nDatePipe,
                        L10nDecimalPipe,
                        L10nPercentPipe,
                        L10nCurrencyPipe,
                        L10nDateDirective,
                        L10nDecimalDirective,
                        L10nPercentDirective,
                        L10nCurrencyDirective
                    ]
                },] }
    ];
    /** @nocollapse */
    LocalizationModule.ctorParameters = function () { return [
        { type: InjectorRef },
        { type: Logger }
    ]; };
    return LocalizationModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides dependencies & directives for validation by locales.
 */
var LocaleValidationModule = /** @class */ (function () {
    function LocaleValidationModule() {
    }
    /**
     * Use in AppModule: new instance of LocaleValidation.
     */
    /**
     * Use in AppModule: new instance of LocaleValidation.
     * @return {?}
     */
    LocaleValidationModule.forRoot = /**
     * Use in AppModule: new instance of LocaleValidation.
     * @return {?}
     */
    function () {
        return {
            ngModule: LocaleValidationModule,
            providers: [LocaleValidation]
        };
    };
    LocaleValidationModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [
                        L10nNumberValidatorDirective
                    ],
                    exports: [
                        L10nNumberValidatorDirective
                    ]
                },] }
    ];
    return LocaleValidationModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides dependencies for sorting and filtering a list by locales.
 */
var CollatorModule = /** @class */ (function () {
    function CollatorModule() {
    }
    CollatorModule.decorators = [
        { type: NgModule, args: [{
                    providers: [Collator]
                },] }
    ];
    return CollatorModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Provides dependencies & components for SEO by locales.
 */
var LocaleSeoModule = /** @class */ (function () {
    function LocaleSeoModule() {
    }
    /**
     * Use in AppModule: new instance of SearchService.
     */
    /**
     * Use in AppModule: new instance of SearchService.
     * @return {?}
     */
    LocaleSeoModule.forRoot = /**
     * Use in AppModule: new instance of SearchService.
     * @return {?}
     */
    function () {
        return {
            ngModule: LocaleSeoModule,
            providers: [
                LocalizedRouting,
                SearchService,
                { provide: L10nLoader, useClass: LocalizedRoutingLoader }
            ]
        };
    };
    /**
     * Use in feature modules with lazy loading: new instance of SearchService.
     */
    /**
     * Use in feature modules with lazy loading: new instance of SearchService.
     * @return {?}
     */
    LocaleSeoModule.forChild = /**
     * Use in feature modules with lazy loading: new instance of SearchService.
     * @return {?}
     */
    function () {
        return {
            ngModule: LocaleSeoModule,
            providers: [
                SearchService
            ]
        };
    };
    LocaleSeoModule.decorators = [
        { type: NgModule, args: [{
                    declarations: [L10nJsonLdComponent],
                    exports: [L10nJsonLdComponent]
                },] }
    ];
    return LocaleSeoModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */
/**
 * Sets locale in 'Accept-Language' header on outgoing requests.
 */
var LocaleInterceptorModule = /** @class */ (function () {
    function LocaleInterceptorModule() {
    }
    LocaleInterceptorModule.decorators = [
        { type: NgModule, args: [{
                    providers: [
                        { provide: HTTP_INTERCEPTORS, useClass: LocaleInterceptor, multi: true }
                    ]
                },] }
    ];
    return LocaleInterceptorModule;
}());

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,uselessCode} checked by tsc
 */

export { InjectorRef, Logger, Caching, StorageStrategy, ProviderType, ISOCode, LogLevel, L10N_CONFIG, l10nConfigFactory, LocalizedRouting, L10nLoader, LocaleLoader, TranslationLoader, LocalizedRoutingLoader, LocaleService, LocaleStorage, L10nStorage, TranslationService, TranslationProvider, L10nTranslationProvider, TranslationHandler, L10nTranslationHandler, Translation, Localization, LocaleValidation, Collator, SearchService, LocaleInterceptor, IntlAPI, Language$1 as Language, DefaultLocale$2 as DefaultLocale, Currency, Timezone, TranslatePipe, L10nDatePipe, L10nDecimalPipe, L10nPercentPipe, L10nCurrencyPipe, BaseDirective, TranslateDirective, L10nDateDirective, L10nDecimalDirective, L10nPercentDirective, L10nCurrencyDirective, L10nNumberValidatorDirective, l10nValidateNumber, L10nJsonLdComponent, TranslationModule, provideRoot, provideChild, LocalizationModule, LocaleValidationModule, CollatorModule, LocaleSeoModule, LocaleInterceptorModule };
//# sourceMappingURL=angular-l10n.js.map
